import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiError, useAuth } from "@/context/AuthContext";
import { StatusBadge, fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CaretLeft, CheckCircle, ArrowRight, WhatsappLogo, Check, X, Eye,
  Paperclip, PencilSimple, ArrowUUpLeft, FileArrowDown, PaperPlaneRight, Trash, WarningCircle, Warning, Plus, Receipt,
  Folder, FolderOpen, CaretDown, CaretRight, ShareNetwork, Copy, FlowArrow, ClockCounterClockwise, LockKey, LockKeyOpen,
} from "@phosphor-icons/react";
import { OrderPurchaseOrders } from "@/components/OrderPurchaseOrders";
import { hasSpecialPermission } from "@/lib/permissions";

const inputCls = "bg-zinc-950 border-zinc-800 rounded-sm text-zinc-50 focus:border-emerald-600 focus-visible:ring-emerald-600";
const label = "text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5 block";
const TYPE_LABEL = { customer_approval: "Customer Approval", material_check: "Material Check", normal: "Task" };
const stagePct = (s) => (s.status === "completed" ? 100 : Number(s.progress ?? s.task_progress ?? 0));

/* Best-effort match between a stage's name and a Masters document type — used to upload straight
   from that stage's popup WITHOUT asking the user to pick from a big dropdown every time. Returns
   null when there's no confident match, so the UI can show a clear "no folder for this yet" alert
   instead of silently filing it under the wrong category. */
const guessDocType = (name, types) => {
  if (!types.length) return null;
  const n = (name || "").toLowerCase();
  const byKeyword = (kw) => types.find((d) => d.name.toLowerCase().includes(kw));
  if (/purchase\s*order|\bpo\b/.test(n)) return byKeyword("po") || null;
  if (/customer approved|approved drawing/.test(n)) return byKeyword("approved drawing") || byKeyword("drawing") || null;
  if (/drawing/.test(n)) return byKeyword("rough drawing") || byKeyword("drawing") || null;
  if (/bom|boq/.test(n)) return byKeyword("bom") || null;
  if (/tender/.test(n)) return byKeyword("tender") || null;
  return null;
};

/* Drawing-type Document Types (Rough/Approved/GA Drawing, etc.) get one upload slot per Account
   Section / Costing line item ("panel") instead of one shared bucket — see the `panels` list and
   panelIndex/panelName args on uploadFor/reviseFor below. */
const isDrawingCat = (name) => /drawing/i.test(name || "");

/* 5 document folders requested by the user, grouping the flat Document Types list by where each
   one belongs in the workflow — Start New Order (catch-all default), PO for Vendors, Drawings and
   Approved Drawings are matched by keyword off the Document Type name; the Account folder isn't a
   Document Type at all — it's populated straight from attachments carrying category "Costing
   Sheet" (added on the Account Section / Costing page). */
const bucketDocTypes = (docTypes) => {
  const b = { new_order: [], po_vendor: [], drawings: [], approved_drawings: [] };
  docTypes.forEach((d) => {
    const n = d.name || "";
    if (/approved/i.test(n) && /drawing/i.test(n)) b.approved_drawings.push(d);
    else if (/drawing/i.test(n)) b.drawings.push(d);
    else if (/\bpo\b/i.test(n) && !/customer/i.test(n)) b.po_vendor.push(d);
    else b.new_order.push(d);
  });
  return b;
};

const Ring = ({ pct }) => {
  const r = 40, c = 2 * Math.PI * r;
  return (
    <div className="relative w-[110px] h-[110px] shrink-0" data-testid="order-progress-ring">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="7" />
        <circle cx="50" cy="50" r={r} fill="none" stroke="#10b981" strokeWidth="7" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-2xl font-black text-zinc-50">{pct}%</span>
        <span className="text-[8px] uppercase tracking-widest text-zinc-400">Complete</span>
      </div>
    </div>
  );
};

const Bar = ({ pct }) => (
  <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mt-3">
    <div className="h-full bg-emerald-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
  </div>
);

/* Traffic-light status used across the stage list: green once done, red once past its due
   date/time without being done, yellow for everything else still on track (pending or active). */
const stageColor = (s) => s.status === "completed" ? "green" : (s.overdue_days > 0 ? "red" : "yellow");

const StageBadge = ({ s }) => {
  const c = stageColor(s);
  if (c === "green") return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-semibold">Completed</span>;
  if (c === "red") return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 font-semibold">Overdue</span>;
  return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 font-semibold">{s.status === "active" ? "In Progress" : "Pending"}</span>;
};

/* A stage counts as a "PO stage" if its name mentions Purchase Order/PO, or it's one of the
   built-in PO stage keys — covers both default and custom Workflow Templates. */
const isPoStage = (s) => /purchase\s*order|\bpo\b/i.test(s?.name || "") || ["po_create", "po_place"].includes(s?.key);
/* The "Upload Customer Order Details For Accounts" stage now opens the full Account Section /
   Costing page directly on click, instead of the generic stage popup — this stage IS
   the Account Section entry point now (the separate header link/tab was removed). */
const isAccountsStage = (s) => /customer\s*order\s*details|account\s*section/i.test(s?.name || "");
/* Any "Upload ..." stage whose completion should be fully driven by document uploads (all
   required items in = Done, some = In Process, none = Not Started) instead of the generic
   manual Submit-Feedback flow. Excludes the Accounts stage (its own save-trigger) and the PO
   stage (its own workspace/3-check-gate lifecycle, not a simple file count). */
const isUploadStage = (s) => /^upload/i.test((s?.name || "").trim()) && !isAccountsStage(s) && !isPoStage(s);
/* The "Upload Customer Approved Drawings" stage — gets the extra combined-PDF upload option and
   is where the Pre-Placement 3-Check Gate summary (server-computed, see po_gate_missing) shows. */
const isApprovedDrawingsStage = (s) => /approved.*drawing/i.test(s?.name || "");

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "workflow");
  // `sel` opens the stage detail panel on a single click on a stage card; it also reads a
  // `?stage=<index>` query param so a shared/bookmarked link lands pre-opened there too.
  const [sel, setSel] = useState(() => { const v = new URLSearchParams(window.location.search).get("stage"); return v !== null ? Number(v) : null; });
  const [fb, setFb] = useState({ progress: "", units_cleared: "", work_completed: "", work_pending: "", blocker: "" });
  const [edit, setEdit] = useState(false);
  const [ef, setEf] = useState({});
  const [capOverrideOpen, setCapOverrideOpen] = useState(false);
  const [capOverrideVal, setCapOverrideVal] = useState("");
  const [capOverrideReason, setCapOverrideReason] = useState("");
  const [showRev, setShowRev] = useState(false);
  const [revNote, setRevNote] = useState("");
  const [skipReasonOpen, setSkipReasonOpen] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const [vendors, setVendors] = useState([]);
  const [vendorRegistry, setVendorRegistry] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeForm, setComposeForm] = useState({ recipientType: "customer", vendorId: "", customEmail: "", customPhone: "", subject: "", text: "", email: true, whatsapp: false, taggedIds: [], ccTag: false });
  const [poItems, setPoItems] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [prev, setPrev] = useState(null);
  const [users, setUsers] = useState([]);
  const [orderTakerIds, setOrderTakerIds] = useState([]);
  const [subTitle, setSubTitle] = useState("");
  const [subAssignee, setSubAssignee] = useState("");
  const [remOpen, setRemOpen] = useState(false);
  const [remRows, setRemRows] = useState([{ channel: "in_app", value: 10, unit: "minutes", when: "before" }]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAssign, setBulkAssign] = useState({});
  const [bulkOwnerAll, setBulkOwnerAll] = useState("");
  const [selectedDrawings, setSelectedDrawings] = useState([]);
  const [checkReqOpen, setCheckReqOpen] = useState(false);
  const [checkReviewer, setCheckReviewer] = useState("");
  const [checkNote, setCheckNote] = useState("");
  const [openFolder, setOpenFolder] = useState(null);
  const [openHistory, setOpenHistory] = useState({});
  const [shareDialog, setShareDialog] = useState(null);
  const [sharingEmail, setSharingEmail] = useState(false);
  const [autoSendPoNo, setAutoSendPoNo] = useState(null);
  const [poFullScreen, setPoFullScreen] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [poGate, setPoGate] = useState(null);
  const [poGateBusy, setPoGateBusy] = useState(false);
  const [customVals, setCustomVals] = useState({});
  const [jobRespEdit, setJobRespEdit] = useState(false);
  const [delayFor, setDelayFor] = useState(null);
  const [delayReason, setDelayReason] = useState("");
  const [delaySuggestions, setDelaySuggestions] = useState([]);
  const [jobRespSel, setJobRespSel] = useState([]);
  const [roleAssignEdit, setRoleAssignEdit] = useState(false);
  const [roleAssignSel, setRoleAssignSel] = useState({ po_incharge: "", drawings_incharge: "", design_incharge: "", production_incharge: "" });
  const [roleAssignUnlocking, setRoleAssignUnlocking] = useState(false);
  const [roleAssignSaving, setRoleAssignSaving] = useState(false);
  const [efShipSame, setEfShipSame] = useState(true);
  const [showShipHistory, setShowShipHistory] = useState(false);
  const [showBillHistory, setShowBillHistory] = useState(false);
  const [showApprovalTrail, setShowApprovalTrail] = useState(false);
  const [featurePermissions, setFeaturePermissions] = useState(null);
  const isManager = user.role !== "member";

  // Stage detail panel opens ONLY when the user clicks a stage on the left — no stage is
  // auto-selected on load, so `sel` stays null until then.
  const load = useCallback(() => api.get(`/orders/${id}`).then((r) => {
    setO(r.data); setPoItems(r.data.po_items || []);
  }).catch(() => toast.error("Order not found")), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/vendor-registry").then((r) => setVendorRegistry(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/document_types").then((r) => setDocTypes(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/users?active=true").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/settings/order-takers").then((r) => setOrderTakerIds(r.data.user_ids || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/settings/order-form").then((r) => setCustomFields(r.data.custom || [])).catch(() => {}); }, []);

  // Deep-link from an "Order Missing Newly-Mandatory Info" email/notification: ?edit=1&focus=<key>
  // auto-opens the Edit Order dialog and scrolls straight to the field that needs filling in.
  const FOCUS_TESTID_MAP = {
    customer: "edit-customer", customer_code: "edit-customer-code", contact_person: "edit-contact-person",
    customer_email: "edit-customer-email", customer_whatsapp: "edit-customer-whatsapp", customer_gst: "edit-customer-gst",
    billing_address: "edit-billing-address", customer_po: "edit-customer-po", order_date: "edit-order-date",
    target_dispatch: "edit-target-dispatch", taken_by: "edit-taken-by",
  };
  useEffect(() => {
    if (!o || !isManager) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("edit") !== "1") return;
    openEdit();
    const focus = params.get("focus");
    if (focus) {
      setTimeout(() => {
        const testid = FOCUS_TESTID_MAP[focus] || `edit-custom-${focus}`;
        const el = document.querySelector(`[data-testid="${testid}"]`);
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); }
      }, 350);
    }
    navigate(`/orders/${id}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o]);

  useEffect(() => {
    api
      .get("/feature-permissions/me")
      .then((response) => setFeaturePermissions(response.data.permissions))
      .catch(() => setFeaturePermissions({}));
  }, []);

  // The Pre-Placement 3-Check Gate lives on the linked PO, not the stage itself — whichever
  // stage dialog surfaces it (Upload PO for Vendor, Drawing Approval, or Take Confirmation From
  // Store & Place PO), fetch that PO's live gate status so the checks can be ticked right here.
  useEffect(() => {
    const poId = o?.stages?.[sel]?.po_gate_po_id;
    if (!poId) { setPoGate(null); return; }
    // Members without a stake in this PO get a 403 on the live gate fetch (write access is
    // intentionally locked down) — fall back to the read-only, role-agnostic po_gate_missing
    // already on the enriched stage instead of leaving the panel stuck on "Loading checks…".
    api.get(`/purchase-orders/${poId}/gate`).then((r) => setPoGate(r.data)).catch(() => setPoGate("fallback"));
  }, [o, sel]);

  if (!o) return <div className="p-8 text-zinc-400 font-mono text-sm">Loading order…</div>;

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const stages = o.stages || [];
  // One "panel" per named Account Section / Costing line item — drives one drawing-upload slot
  // per panel instead of a single shared bucket (index kept from the un-filtered list so it stays
  // a stable key even if a blank row sits ahead of a named one).
  const panels = (o.costing?.line_items || [])
    .map((li, i) => ({ index: i, name: (li.name || "").trim() }))
    .filter((p) => p.name);
  // Uploads always tag the trimmed name (matches what's shown as the panel label), so any future
  // filtering/joins against panel_name stay consistent with the display value.
  const current = stages.find((s) => s.status === "active");
  const approved = stages.some((s) => s.type === "customer_approval" && s.status === "completed");
  const projectRef = (o.details || []).find((d) => d.title === "Project Reference")?.value;
  const matStages = stages.map((s, i) => ({ ...s, idx: i })).filter((s) => s.type === "material_check");
  const totalDelays = stages.reduce((n, s) => n + (s.delays || []).length, 0);
  const selIdx = sel !== null && sel < stages.length ? sel : null;
  const S = selIdx !== null ? stages[selIdx] : null;
  // Shop-floor stages run concurrently as one pipeline, but a later station can never be
  // reported/completed further than however much material the station before it has cleared.
  // If the order has a `total_units` count, the cap is tracked in actual units (more precise);
  // an Admin's `cap_override` on the stage always wins over the computed cap.
  const mfgCap = (() => {
    if (!S || S.category !== "Manufacturing" || selIdx === 0) return null;
    const prev = stages[selIdx - 1];
    if (!prev || prev.category !== "Manufacturing") return null;
    const totalUnits = o.total_units;
    if (S.cap_override != null) {
      const ov = S.cap_override;
      return { pct: totalUnits ? Math.round((ov / totalUnits) * 100) : ov, units: totalUnits ? ov : null,
               prevName: prev.name, overridden: true, reason: S.cap_override_reason, by: S.cap_override_by, at: S.cap_override_at };
    }
    const prevDone = prev.status === "completed";
    if (totalUnits) {
      const units = prevDone ? totalUnits : (prev.units_cleared || 0);
      return { pct: Math.round((units / totalUnits) * 100), units, prevName: prev.name, overridden: false };
    }
    return { pct: prevDone ? 100 : (prev.progress || 0), units: null, prevName: prev.name, overridden: false };
  })();
  // For an "Upload ..." stage, completion is driven by document/panel upload counts rather than
  // manual feedback — this helper takes any stage (used for the currently-open dialog's S below,
  // and also for the header Advance Stage guardrail, which needs to check ALL active stages).
  const getUploadStageInfo = (stg) => {
    if (!stg || !isUploadStage(stg) || !docTypes.length) return null;
    const guessed = guessDocType(stg.name, docTypes);
    const activeCat = guessed?.name;
    if (!activeCat) return null;
    const catFiles = (o.attachments || []).filter((a) => a.category === activeCat);
    const drawingPanels = isDrawingCat(activeCat) ? panels : [];
    const required = drawingPanels.length > 0 ? drawingPanels.length : 1;
    const done = drawingPanels.length > 0
      ? drawingPanels.filter((p) => catFiles.some((a) => a.panel_index === p.index)).length
      : (catFiles.length > 0 ? 1 : 0);
    // "Upload Customer Approved Drawings" isn't done just because a file exists — it specifically
    // needs the CUSTOMER sign-off (approval_status) on that file, so track that separately too.
    const isApproval = isApprovedDrawingsStage(stg);
    const approvedCount = isApproval ? catFiles.filter((a) => a.approval_status === "approved").length : null;
    // For an approval-type upload stage, "Progress" must reflect actual customer sign-off
    // (approvedCount), not just that a file was uploaded (done) — otherwise the stat shows
    // "100%" the moment the last drawing is uploaded while the stage is still stuck at
    // pending/yellow waiting on real approval, which reads as a contradictory stuck state.
    const numerator = isApproval ? (approvedCount || 0) : done;
    return { activeCat, required, done, approvedCount, isApproval, pct: required > 0 ? Math.round((numerator / required) * 100) : 0 };
  };
  const uploadStageInfo = getUploadStageInfo(S);
  const canManagePO = isManager || o.created_by === user.id || stages.some((s) => s.assignee === user.id)
    || (o.job_responsibility || []).includes(user.id) || hasSpecialPermission(user, "costing_override");
  const canApproveDrawings = Boolean(featurePermissions?.approve_drawings);
  const canCreatePurchaseOrders = Boolean(featurePermissions?.create_purchase_orders);
  // Matches the backend's set_po_gate_check permission exactly (admin/manager/costing_override only)
  // — narrower than canManagePO, which also lets a creator/assignee view the PO panel.
  const canGateCheck = user.role === "admin" || user.role === "manager" || hasSpecialPermission(user, "costing_override");
  const togglePoGateCheck = async (check, done) => {
    if (!S?.po_gate_po_id) return;
    setPoGateBusy(true);
    try { const { data } = await api.post(`/purchase-orders/${S.po_gate_po_id}/gate/${check}`, { done }); setPoGate(data); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not update check"); }
    finally { setPoGateBusy(false); }
  };
  // Job Responsibility is assigned at intake, but Admin/Manager/HR — or anyone explicitly granted
  // the "job_responsibility_edit" permission — can revise it later from this card.
  const canEditJobResp = ["admin", "manager", "hr"].includes(user.role) || hasSpecialPermission(user, "job_responsibility_edit");
  const openJobResp = () => { setJobRespSel(o.job_responsibility || []); setJobRespEdit(true); };
  const saveJobResp = async () => {
    if (!jobRespSel.length) return toast.error("Select at least one responsible person");
    await act(() => api.put(`/orders/${id}/job-responsibility`, { job_responsibility: jobRespSel }), "Job Responsibility updated");
    setJobRespEdit(false);
  };

  // Task Allotment — the 4 named in-charge roles. Locked by default (like the Costing Sheet);
  // Admin/Manager must explicitly unlock before the fields become editable, which then re-locks
  // automatically on Save so nobody can amend it by accident.
  const canUnlockRoleAssign = user.role === "admin" || user.role === "manager";
  const unlockRoleAssign = async () => {
    setRoleAssignUnlocking(true);
    try {
      await api.post(`/orders/${id}/role-assignments/unlock`);
      setRoleAssignSel({
        po_incharge: Array.isArray(o.role_assignments?.po_incharge)
          ? o.role_assignments.po_incharge[0] || ""
          : o.role_assignments?.po_incharge || "",
        drawings_incharge: Array.isArray(o.role_assignments?.drawings_incharge)
          ? o.role_assignments.drawings_incharge[0] || ""
          : o.role_assignments?.drawings_incharge || "",
        design_incharge: Array.isArray(o.role_assignments?.design_incharge)
          ? o.role_assignments.design_incharge[0] || ""
          : o.role_assignments?.design_incharge || "",
        production_incharge: Array.isArray(o.role_assignments?.production_incharge)
          ? o.role_assignments.production_incharge[0] || ""
          : o.role_assignments?.production_incharge || "",
      });
      setRoleAssignEdit(true);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not unlock Task Allotment"); }
    finally { setRoleAssignUnlocking(false); }
  };
  const saveRoleAssign = async () => {
    setRoleAssignSaving(true);
    try {
      await api.put(`/orders/${id}/role-assignments`, {
        po_incharge: roleAssignSel.po_incharge || null,
        drawings_incharge: roleAssignSel.drawings_incharge || null,
        design_incharge: roleAssignSel.design_incharge || null,
        production_incharge: roleAssignSel.production_incharge || null,
      });
      toast.success("Task Allotment saved — locked again");
      setRoleAssignEdit(false);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not save Task Allotment"); }
    finally { setRoleAssignSaving(false); }
  };

  const submitFb = async () => {
    const unitMode = o.total_units && S?.category === "Manufacturing";
    if (unitMode) {
      if (fb.units_cleared === "" && !fb.work_completed && !fb.work_pending && !fb.blocker) return toast.error("Add units cleared or notes first");
    } else if (fb.progress === "" && !fb.work_completed && !fb.work_pending && !fb.blocker) return toast.error("Add progress or notes first");
    try {
      await api.post(`/orders/${id}/stage/${selIdx}/feedback`, {
        progress: Number(fb.progress) || 0,
        units_cleared: unitMode && fb.units_cleared !== "" ? Number(fb.units_cleared) : undefined,
        work_completed: fb.work_completed || null, work_pending: fb.work_pending || null, blocker: fb.blocker || null,
      });
      toast.success("Progress feedback recorded");
      setFb({ progress: "", units_cleared: "", work_completed: "", work_pending: "", blocker: "" });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const completeMfgStage = async () => {
    try {
      await api.post(`/orders/${id}/advance`, { index: selIdx });
      toast.success(`"${S.name}" marked complete`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const setCapOverride = async () => {
    if (capOverrideVal === "" || !capOverrideReason.trim()) return toast.error("Enter a value and a reason");
    try {
      await api.post(`/orders/${id}/stage/${selIdx}/cap-override`, { value: Number(capOverrideVal), reason: capOverrideReason.trim() });
      toast.success("Cap override set");
      setCapOverrideOpen(false); setCapOverrideVal(""); setCapOverrideReason("");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const clearCapOverride = async () => {
    try { await api.delete(`/orders/${id}/stage/${selIdx}/cap-override`); toast.success("Cap override cleared"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const uploadFor = (cat, e, panelIndex, panelName, replacesId) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) return toast.error("Max 50MB per file");
    const r = new FileReader();
    r.onload = async () => {
      try {
        await api.post(`/orders/${id}/attachments`, {
          name: file.name, type: file.type, size: file.size, data: r.result, category: cat,
          panel_index: panelIndex ?? null, panel_name: panelName ?? null,
          ...(replacesId ? { replaces_id: replacesId } : {}),
        });
        toast.success(replacesId ? "New version uploaded — previous version kept under Version history" : `${cat} uploaded`);
        load();
      } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed — try a smaller file"); }
    };
    r.onerror = () => toast.error("Could not read the file");
    r.readAsDataURL(file);
    e.target.value = "";
  };
  const getFileData = async (a) => {
    if (a.data) return a.data;
    const { data } = await api.get(`/orders/${id}/attachments/${a.id}/data`);
    return data.data;
  };
  const viewFile = async (a) => {
    const w = window.open("about:blank", "_blank"); // open synchronously so browsers don't block the popup
    try {
      const dataUrl = await getFileData(a);
      const parts = dataUrl.split(",");
      const mime = (parts[0].match(/data:(.*?);/) || [])[1] || "application/octet-stream";
      const bin = atob(parts[1]);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: mime }));
      if (w && !w.closed) w.location.href = url;
      else window.open(url, "_blank");
    } catch (err) {
      if (w && !w.closed) w.close();
      const detail = apiError(err.response?.data?.detail);
      if (err.response?.status === 403) toast.error(detail);
      else { toast.error("Could not open this file — downloading instead"); downloadFile(a); }
    }
  };
  const downloadFile = async (a) => {
    try {
      const dataUrl = await getFileData(a);
      const el = document.createElement("a");
      el.href = dataUrl; el.download = a.name; el.click();
    } catch { toast.error("Could not download this file"); }
  };
  const delAtt = (aid) => act(() => api.delete(`/orders/${id}/attachments/${aid}`), "File removed");
  // Secure Drawing Link — generate (or reuse) a no-login public link for a drawing, so it can be
  // copied or emailed straight to the customer (View/Download/Alert-me-on-changes/Approve).
  const openShare = async (a) => {
    try {
      const { data } = await api.post(`/orders/${id}/attachments/${a.id}/share-link`);
      setShareDialog({ attachment: a, url: data.url, emailTo: o.customer_email || "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not generate a share link");
    }
  };
  const sendShareEmail = async () => {
    if (!shareDialog) return;
    setSharingEmail(true);
    try {
      await api.post(`/orders/${id}/attachments/${shareDialog.attachment.id}/share-link/email`, { to: shareDialog.emailTo || undefined });
      toast.success(`Sending drawing link to ${shareDialog.emailTo}…`);
      setShareDialog(null);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not send the email");
    } finally {
      setSharingEmail(false);
    }
  };
  // "Revise" a panel's drawing — uploads the replacement tagged with which version it supersedes.
  // The OLD file is NEVER deleted: the backend just flags it superseded so it drops out of the
  // "current" file for completion/approval checks, while staying fully intact under "Version
  // history" (still viewable/downloadable) on that panel.
  const reviseFor = (cat, e, panelIndex, panelName, oldId) => uploadFor(cat, e, panelIndex, panelName, oldId);
  const reviewDoc = (a, decision) => {
    const remarks = window.prompt(`${decision === "approved" ? "Approve" : "Reject"} "${a.name}" — remarks (optional):`, "");
    if (remarks === null) return;
    act(() => api.post(`/orders/${id}/attachments/${a.id}/review`, { decision, remarks }), `Document ${decision}`);
  };
  // Bulk-download the checked drawings — asks which folder to save them into (File System Access
  // API) instead of silently dumping them one-by-one into the browser's default Downloads folder;
  // falls back to the normal per-file download when the browser doesn't support folder picking.
  const downloadSelected = async () => {
    const files = (o.attachments || []).filter((a) => selectedDrawings.includes(a.id));
    if (!files.length) return;
    if (window.showDirectoryPicker) {
      try {
        const dirHandle = await window.showDirectoryPicker();
        for (const a of files) {
          const dataUrl = await getFileData(a);
          const bin = atob(dataUrl.split(",")[1]);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          const fh = await dirHandle.getFileHandle(a.name, { create: true });
          const writable = await fh.createWritable();
          await writable.write(arr);
          await writable.close();
        }
        toast.success(`${files.length} file${files.length > 1 ? "s" : ""} saved to the chosen folder`);
        return;
      } catch (err) {
        if (err?.name === "AbortError") return; // user cancelled the folder picker — do nothing
        toast.error("Could not use the folder picker here — downloading normally instead.");
        // fall through to the per-file download below
      }
    } else {
      toast.info("This browser can't ask which folder to use — saving to your default Downloads folder instead.");
    }
    for (const a of files) await downloadFile(a);
  };
  const submitCheckRequest = async () => {
    if (!checkReviewer) return toast.error("Pick a colleague first");
    try {
      await Promise.all(selectedDrawings.map((aid) => api.post(`/orders/${id}/attachments/${aid}/request-check`, { reviewer_id: checkReviewer, note: checkNote || null })));
      toast.success(`Check request sent to ${users.find((u) => u.id === checkReviewer)?.name || "the reviewer"}`);
      setCheckReqOpen(false); setCheckReviewer(""); setCheckNote(""); setSelectedDrawings([]);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openEdit = () => {
    const customLabels = new Set(customFields.map((c) => c.label));
    const cVals = {};
    customFields.forEach((c) => {
      const d = (o.details || []).find((x) => x.title === c.label);
      if (d) cVals[c.id] = d.value;
    });
    setCustomVals(cVals);
    setEfShipSame(!o.shipping_address);
    setEf({
      name: o.name, customer: o.customer || "", customer_code: o.customer_code || "",
      customer_email: o.customer_email || "", customer_whatsapp: o.customer_whatsapp || "",
      contact_person: o.contact_person || "", customer_gst: o.customer_gst || "", billing_address: o.billing_address || "",
      shipping_address: o.shipping_address || "",
      customer_po: o.customer_po || "", order_date: (o.order_date || "").slice(0, 10),
      commitment_mode: o.commitment_mode || "fixed", commitment_days: o.commitment_days || null,
      target_dispatch: (o.target_dispatch || "").slice(0, 10), taken_by: o.taken_by || "",
      total_units: o.total_units || "",
      reminders_enabled: o.reminders_enabled !== false, reminder_value: o.reminder_value || 2, reminder_unit: o.reminder_unit || "days",
      details: (o.details || []).filter((d) => !customLabels.has(d.title)).map((d) => ({ ...d })),
    });
    setEdit(true);
  };
  const saveEdit = async () => {
    for (const c of customFields) {
      if (c.required && !(customVals[c.id] || "").trim()) return toast.error(`${c.label} is required`);
    }
    const customDetails = customFields.map((c) => ({ title: c.label, value: (customVals[c.id] || "").trim() })).filter((d) => d.value);
    try {
      await api.put(`/orders/${id}`, { ...ef, shipping_address: efShipSame ? "" : ef.shipping_address, total_units: ef.total_units === "" ? null : ef.total_units, details: [...(ef.details || []).filter((d) => d.title), ...customDetails] });
      toast.success("Order updated"); setEdit(false); load();
    }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const requestRevision = async () => {
    try { await api.post(`/orders/${id}/request-revision`, { note: revNote }); toast.success("Sent back to drawing for revision"); setShowRev(false); setRevNote(""); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const skipDesign = async () => {
    if (!skipReason.trim()) { toast.error("Enter a reason before skipping Design & Nesting"); return; }
    try {
      await api.post(`/orders/${id}/stage/${selIdx}/skip-design`, { reason: skipReason.trim() });
      toast.success("Skipped — sent direct to Cutting"); setSkipReasonOpen(false); setSkipReason(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const efDetail = (i, k, v) => setEf((p) => ({ ...p, details: p.details.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)) }));

  // "Correspondence" — one composer for email/WhatsApp to the customer, a vendor, or a typed-in
  // contact, with the option to tag teammates (in-app, optionally CC'd on the actual email too).
  const pickVField = (v, cands) => {
    for (const c of cands) {
      const key = Object.keys(v.data || {}).find((k) => k.toLowerCase().includes(c));
      if (key && v.data[key]) return v.data[key];
    }
    return "";
  };
  const vTitle = (v) => pickVField(v, ["vendor_name", "name", "company"]) || "Vendor";
  const sendCompose = async () => {
    const f = composeForm;
    if (!f.text.trim()) return toast.error("Write a message first");
    if (!f.email && !f.whatsapp) return toast.error("Pick at least one channel — Email or WhatsApp");
    const channels = ["in_app"].concat(f.email ? ["email"] : [], f.whatsapp ? ["whatsapp"] : []);
    let recipient_label, recipient_email, recipient_phone;
    if (f.recipientType === "customer") {
      recipient_label = o.customer || o.name; recipient_email = o.customer_email; recipient_phone = o.customer_whatsapp;
    } else if (f.recipientType === "vendor") {
      const v = vendorRegistry.find((x) => x.id === f.vendorId);
      recipient_label = v ? vTitle(v) : "Vendor"; recipient_email = v ? pickVField(v, ["email"]) : ""; recipient_phone = v ? pickVField(v, ["mobile", "phone", "whatsapp"]) : "";
    } else {
      recipient_label = f.customEmail || f.customPhone || "Contact"; recipient_email = f.customEmail; recipient_phone = f.customPhone;
    }
    try {
      await api.post(`/orders/${id}/messages`, {
        text: f.text, subject: f.subject || null, channels,
        recipient_type: f.recipientType, recipient_label, recipient_email: recipient_email || null, recipient_phone: recipient_phone || null,
        tagged_user_ids: f.taggedIds, cc_tagged: f.ccTag,
      });
      toast.success("Sent");
      setComposeOpen(false);
      setComposeForm({ recipientType: "customer", vendorId: "", customEmail: "", customPhone: "", subject: "", text: "", email: true, whatsapp: false, taggedIds: [], ccTag: false });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const logDelay = (i) => {
    setDelayReason("");
    setDelayFor(i);
    if (delaySuggestions.length === 0) {
      api.get("/orders/delay-reason-suggestions").then((r) => setDelaySuggestions(r.data || [])).catch(() => {});
    }
  };
  const submitDelay = async () => {
    if (!delayReason.trim()) return toast.error("Enter a reason for the delay");
    await act(() => api.post(`/orders/${id}/stage/${delayFor}/delay`, { reason: delayReason.trim() }), "Delay logged");
    setDelayFor(null);
  };
  // Guardrail: before advancing, warn (don't silently let through) if any currently-active
  // upload-type stage still shows its document/panel(s) missing — this catches optional (not
  // globally "required") document stages that the backend's block_if_missing_docs wouldn't
  // otherwise stop, e.g. an order-specific "Upload Drawings" that was never marked mandatory.
  const advanceStage = () => {
    const missing = (o.stages || [])
      .filter((s) => s.status === "active")
      .map((s) => ({ s, info: getUploadStageInfo(s) }))
      .filter(({ info }) => info && (info.done < info.required || (info.isApproval && info.approvedCount < info.done)));
    if (missing.length > 0) {
      const lines = missing.map(({ s, info }) => {
        if (info.done < info.required) return `"${s.name}" (${info.done}/${info.required} uploaded)`;
        return `"${s.name}" — drawing(s) uploaded but customer sign-off/approval is still pending (${info.approvedCount}/${info.done} approved)`;
      }).join(", ");
      if (!window.confirm(`⚠ Still missing: ${lines}.\n\nAdvance anyway?`)) return;
    }
    act(() => api.post(`/orders/${id}/advance`), "Advanced to next stage");
  };
  const genPO = async () => {
    try {
      const res = await api.get(`/orders/${id}/po`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `PO_${o.order_no}.xlsx`; a.click(); URL.revokeObjectURL(url);
      toast.success("PO generated");
    } catch { toast.error("PO generation failed"); }
  };
  const printDeliveryNote = async () => {
    const w = window.open("about:blank", "_blank");
    try {
      const res = await api.get(`/orders/${id}/delivery-note/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (w && !w.closed) w.location.href = url; else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (w && !w.closed) w.close();
      toast.error(apiError(err.response?.data?.detail) || "Could not generate the Delivery Note");
    }
  };
  const previewPO = async () => {
    try { const { data } = await api.get(`/orders/${id}/po/preview`); setPrev(data); }
    catch { toast.error("Preview failed"); }
  };
  const addSubTask = async () => {
    if (!subTitle.trim() || !subAssignee) return toast.error("Sub-task title and member required");
    try {
      await api.post("/tasks", { title: subTitle, parent_id: S.task_id, priority: "normal", assignees: [{ user_id: subAssignee }] });
      toast.success("Sub-task assigned"); setSubTitle(""); setSubAssignee(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const nudgeSubTask = async (c) => {
    try { await api.post(`/tasks/${c.id}/nudge`, { channel: "both" }); toast.success(`Reminder sent to ${c.assignee || "the assignee"}`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const updRem = (i, k, v) => setRemRows(remRows.map((r, xi) => (xi === i ? { ...r, [k]: v } : r)));
  const saveReminders = async () => {
    try {
      const { data } = await api.post(`/tasks/${S.task_id}/reminders`, { reminders: remRows.map((r) => ({ ...r, value: Number(r.value) || 0 })) });
      toast.success(`${data.count} reminder(s) scheduled`); setRemOpen(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const openBulkAssign = () => { setBulkAssign({}); setBulkOwnerAll(""); setBulkOpen(true); };
  const saveBulkAssign = async () => {
    const assignments = Object.entries(bulkAssign).filter(([, v]) => v).map(([index, user_id]) => ({ index: Number(index), user_id }));
    if (assignments.length === 0) return toast.error("Pick at least one stage to assign");
    try {
      await api.post(`/orders/${id}/stages/assign-bulk`, { assignments });
      toast.success(`${assignments.length} stage(s) assigned`); setBulkOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const setVendor = async (v) => { await api.put(`/orders/${id}`, { po_vendor: v }); toast.success("Vendor set"); load(); };
  const addItem = () => setPoItems((p) => [...p, { desc: "", qty: 1, rate: 0 }]);
  const updItem = (i, k, v) => setPoItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const delItem = (i) => setPoItems((p) => p.filter((_, idx) => idx !== i));
  const saveItems = async () => {
    const clean = poItems.filter((it) => (it.desc || "").trim()).map((it) => ({ desc: it.desc, qty: Number(it.qty) || 0, rate: Number(it.rate) || 0 }));
    try { await api.put(`/orders/${id}`, { po_items: clean }); toast.success("Line items saved"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const poTotal = poItems.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);

  const tabBtn = (key, name, count) => (
    <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
      className={`px-4 py-2 rounded-sm border text-sm font-semibold transition-colors flex items-center gap-2 ${tab === key ? "border-emerald-600/60 bg-emerald-600/10 text-emerald-400" : "border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:text-zinc-200"}`}>
      {name} <span className={`text-xs font-mono ${tab === key ? "text-emerald-400" : "text-zinc-500"}`}>{count}</span>
    </button>
  );

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate("/orders")} data-testid="back-to-orders" className="text-sm text-zinc-400 hover:text-emerald-400 flex items-center gap-1 transition-colors">
          <CaretLeft size={14} /> Back to orders
        </button>
        <button onClick={() => navigate("/orders?new=1")} data-testid="start-new-order-btn" className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1 font-semibold transition-colors">
          <Plus size={14} weight="bold" /> Start New Order
        </button>
      </div>

      {/* Unified order header — company identity, stage, progress and all key details in ONE
          place (previously split across a plain title row + a separate hero banner + a separate
          info strip, which repeated the company name and Customer PO twice). */}
      <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-emerald-950 via-teal-900 to-slate-900 border border-emerald-700/50 p-6 mb-4 shadow-lg" data-testid="order-hero">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold mb-2 bg-emerald-500/20 border border-emerald-500/40 rounded-full px-2.5 py-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {o.order_no}
        </div>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-2xl lg:text-3xl font-extrabold tracking-tight text-white truncate" data-testid="order-customer-name">{o.customer || o.name}</h1>
            <div className="flex items-center gap-6 flex-wrap mt-1.5" data-testid="order-codes">
              {o.customer_code && (
                <div data-testid="order-ref-code">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-200/60">Ref Code</div>
                  <div className="font-heading text-2xl lg:text-3xl font-black tracking-tight text-emerald-300">{o.customer_code}</div>
                </div>
              )}
              {projectRef && (
                <div data-testid="order-project-ref">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-200/60">Project Reference</div>
                  <div className="font-heading text-2xl lg:text-3xl font-black tracking-tight text-emerald-300">{projectRef}</div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2.5">
              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/40">
                {o.status === "completed" ? "Completed" : current ? current.name : "Active"}
              </span>
              {approved && <span className="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/40">Drawing Approved</span>}
              {totalDelays > 0 && <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-500/20 text-amber-200 border border-amber-400/40">{totalDelays} delay{totalDelays > 1 ? "s" : ""} logged</span>}
            </div>
            <p className="text-sm text-emerald-100/70 mt-2.5" data-testid="order-stage-status-line">
              {o.status === "completed" ? "All workflow stages completed." : current ? `${current.department || "Team"} is working on "${current.name}".` : "Workflow not started."}
              {current?.assignee_name && (
                <span className="text-emerald-100/90"> Project Incharge for any information: <span className="font-semibold text-emerald-300" data-testid="order-project-incharge">{current.assignee_name}</span></span>
              )}
            </p>
            {(o.approval_trail || []).length > 0 && (
              <div className="mt-2">
                <button type="button" onClick={() => setShowApprovalTrail((s) => !s)} data-testid="approval-trail-toggle" className="text-[11px] text-emerald-200/60 hover:text-emerald-100 flex items-center gap-1">
                  <ClockCounterClockwise size={11} /> {showApprovalTrail ? "Hide" : "Show"} approval sign-off record ({o.approval_trail.length})
                </button>
                {showApprovalTrail && (
                  <div className="mt-2 border-t border-emerald-700/40 pt-2 space-y-1.5" data-testid="approval-trail-list">
                    {o.approval_trail.map((t, i) => (
                      <div key={i} className="text-[11px] text-emerald-200/60" data-testid={`approval-trail-row-${i}`}>
                        <span className="text-emerald-300 font-semibold">{t.kind === "customer_signoff" ? "Customer sign-off" : "Approved"}</span>
                        {" — \""}{t.label}{"\" by "}<span className="text-emerald-100">{t.by}</span>{t.at ? ` on ${fmtDate(t.at)}` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-3 shrink-0">
            <Ring pct={o.progress} />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setComposeOpen(true)} data-testid="open-compose" className="border-emerald-400/40 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20 rounded-sm gap-1"><PaperPlaneRight size={14} weight="fill" /> Compose</Button>
              {isManager && <Button onClick={openEdit} variant="outline" size="sm" data-testid="edit-order-btn" className="border-emerald-400/30 bg-slate-900/40 text-emerald-100 hover:bg-slate-900/60 rounded-sm gap-1"><PencilSimple size={14} /> Edit</Button>}
              {isManager && o.status !== "completed" && (
                <Button size="sm" onClick={advanceStage} data-testid="advance-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold gap-1">
                  Advance Stage <ArrowRight size={14} weight="bold" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Billing/Shipping Address + Job Responsibility — always visible on the order page.
          Job Responsibility can be reassigned later by Admin/Manager/HR or anyone explicitly
          granted the "job_responsibility_edit" permission (see Permissions page). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="border border-zinc-800 bg-zinc-900 rounded-sm p-4" data-testid="address-card">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Billing &amp; Shipping</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-600 mb-0.5">Billing Address</div>
              <div className="text-sm text-zinc-200" data-testid="view-billing-address">{o.billing_address || "Inherited from Customer Master"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-600 mb-0.5">Shipping Address</div>
              <div className="text-sm text-zinc-200" data-testid="view-shipping-address">{o.shipping_address || "Same as Billing Address"}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {isManager && <button type="button" onClick={openEdit} className="text-[11px] text-emerald-400 hover:underline">Edit addresses →</button>}
            <button type="button" onClick={printDeliveryNote} data-testid="print-delivery-note-btn" className="text-[11px] text-sky-400 hover:underline flex items-center gap-1"><FileArrowDown size={11} /> Download Delivery Note</button>
            {(o.billing_address_history || []).length > 0 && (
              <button type="button" onClick={() => setShowBillHistory((s) => !s)} data-testid="billing-history-toggle" className="text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
                <ClockCounterClockwise size={11} /> {showBillHistory ? "Hide" : "Show"} billing address history ({o.billing_address_history.length})
              </button>
            )}
            {(o.shipping_address_history || []).length > 0 && (
              <button type="button" onClick={() => setShowShipHistory((s) => !s)} data-testid="shipping-history-toggle" className="text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
                <ClockCounterClockwise size={11} /> {showShipHistory ? "Hide" : "Show"} shipping address history ({o.shipping_address_history.length})
              </button>
            )}
          </div>
          {showBillHistory && (o.billing_address_history || []).length > 0 && (
            <div className="mt-2 border-t border-zinc-800 pt-2 space-y-1.5" data-testid="billing-history-list">
              {[...o.billing_address_history].reverse().map((h, i) => (
                <div key={i} className="text-[11px] text-zinc-500" data-testid={`billing-history-row-${i}`}>
                  <span className="text-zinc-300">{h.address}</span> — changed by <span className="text-zinc-400">{h.changed_by_name || "—"}</span> on {new Date(h.changed_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              ))}
            </div>
          )}
          {showShipHistory && (o.shipping_address_history || []).length > 0 && (
            <div className="mt-2 border-t border-zinc-800 pt-2 space-y-1.5" data-testid="shipping-history-list">
              {[...o.shipping_address_history].reverse().map((h, i) => (
                <div key={i} className="text-[11px] text-zinc-500" data-testid={`shipping-history-row-${i}`}>
                  <span className="text-zinc-300">{h.address}</span> — changed by <span className="text-zinc-400">{h.changed_by_name || "—"}</span> on {new Date(h.changed_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-zinc-800 bg-zinc-900 rounded-sm p-4" data-testid="job-resp-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Job Responsibility</div>
            {canEditJobResp && !jobRespEdit && <button type="button" onClick={openJobResp} data-testid="job-resp-edit-btn" className="text-[11px] text-emerald-400 hover:underline flex items-center gap-1"><PencilSimple size={11} /> Edit</button>}
          </div>
          {!jobRespEdit ? (
            <div className="flex flex-wrap gap-1.5" data-testid="job-resp-view">
              {(o.job_responsibility_names || []).length ? (o.job_responsibility_names || []).map((nm, i) => (
                <span key={i} className="inline-flex items-center text-[11px] bg-zinc-800 border border-zinc-700 rounded-full px-2 py-0.5 text-zinc-200">{nm}</span>
              )) : <span className="text-xs text-zinc-600">No one assigned yet.</span>}
              {!canEditJobResp && <p className="text-[10px] text-zinc-600 mt-1 w-full">Only Admin, Manager, HR or an authorized team member can change this.</p>}
            </div>
          ) : (
            <div data-testid="job-resp-editor">
              <div className="border border-zinc-800 rounded-sm max-h-48 overflow-y-auto divide-y divide-zinc-900 mb-2">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-zinc-800/60" data-testid={`job-resp-edit-checkbox-${u.id}`}>
                    <input type="checkbox" checked={jobRespSel.includes(u.id)}
                      onChange={() => setJobRespSel(jobRespSel.includes(u.id) ? jobRespSel.filter((x) => x !== u.id) : [...jobRespSel, u.id])}
                      className="accent-emerald-600" />
                    <span className="text-zinc-200">{u.name}</span>
                    <span className="text-zinc-600">· {u.department || u.role}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={saveJobResp} data-testid="job-resp-save-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-7 text-xs">Save</Button>
                <Button size="sm" variant="outline" onClick={() => setJobRespEdit(false)} data-testid="job-resp-cancel-btn" className="border-zinc-800 rounded-sm h-7 text-xs">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Task Allotment — 4 named in-charge roles. Locked/read-only by default; only Admin or
          Manager can unlock it to change who's responsible, preventing accidental edits. */}
      <div className="border border-zinc-800 bg-zinc-900 rounded-sm p-4 mb-4" data-testid="role-assign-card">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
            {o.role_assignments_locked === false ? <LockKeyOpen size={12} className="text-amber-500" /> : <LockKey size={12} className="text-zinc-500" />} Task Allotment
          </div>
          {canUnlockRoleAssign && !roleAssignEdit && (
            <button type="button" onClick={unlockRoleAssign} disabled={roleAssignUnlocking} data-testid="role-assign-unlock-btn"
              className="text-[11px] text-emerald-400 hover:underline flex items-center gap-1 disabled:opacity-50">
              <LockKeyOpen size={11} /> {roleAssignUnlocking ? "Unlocking…" : "Unlock to Edit"}
            </button>
          )}
        </div>
        {!roleAssignEdit ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="role-assign-view">
            {Object.entries(o.role_assignments_names || {}).map(([k, members]) => (
              <div key={k} data-testid={`role-assign-${k}`}>
                <div className="text-[10px] text-zinc-500">
                  {(o.role_definitions || []).find((role) => role.key === k)?.label || k.replaceAll("_", " ")}
                </div>
                <div className="text-sm text-zinc-200">
                  {Array.isArray(members) ? members.join(", ") : members || <span className="text-zinc-600">Unassigned</span>}
                </div>
              </div>
            ))}
            {!canUnlockRoleAssign && <p className="text-[10px] text-zinc-600 mt-1 sm:col-span-2 lg:col-span-4">Only Admin or Manager can unlock this to make changes.</p>}
          </div>
        ) : (
          <div data-testid="role-assign-editor">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              {[["po_incharge", "PO Incharge"], ["drawings_incharge", "Drawings Incharge"], ["design_incharge", "Design Incharge"], ["production_incharge", "Production Incharge"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-[10px] text-zinc-500 mb-1 block">{l}</label>
                  <Select value={roleAssignSel[k] || ""} onValueChange={(v) => setRoleAssignSel((p) => ({ ...p, [k]: v }))}>
                    <SelectTrigger className={inputCls} data-testid={`role-assign-select-${k}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={saveRoleAssign} disabled={roleAssignSaving} data-testid="role-assign-save-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-7 text-xs">{roleAssignSaving ? "Saving…" : "Save"}</Button>
              <Button size="sm" variant="outline" onClick={() => setRoleAssignEdit(false)} data-testid="role-assign-cancel-btn" className="border-zinc-800 rounded-sm h-7 text-xs">Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-5">
        <div className="flex items-center gap-2 flex-wrap">
          {tabBtn("workflow", "Workflow", stages.length)}
          {tabBtn("docs", "Documents & approvals", (o.attachments || []).length)}
          {tabBtn("material", "Material register", matStages.length + poItems.length)}
          {tabBtn("comms", "Communication & audit", (o.messages || []).length + totalDelays)}
        </div>
        {isManager && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline"
              onClick={() => { if (window.confirm("Pull in any renamed/new stages from the current Workflow Template into this order? Progress, assignees and task history on existing stages are kept — only their name/department/hours etc. get refreshed, and brand-new template stages are added as pending.")) act(() => api.post(`/orders/${id}/sync-template`), "Order synced with the latest Workflow Template"); }}
              data-testid="sync-template-btn" className="border-zinc-800 text-zinc-300 bg-zinc-950 rounded-sm text-xs gap-1">
              <ArrowUUpLeft size={13} /> Sync with Template
            </Button>
            <Button size="sm" variant="outline" onClick={openBulkAssign} data-testid="bulk-assign-btn" className="border-emerald-600/30 text-emerald-400 bg-emerald-600/5 rounded-sm text-xs gap-1">
              <PencilSimple size={13} /> Assign All Stages
            </Button>
          </div>
        )}
      </div>

      {/* ------- WORKFLOW TAB ------- */}
      {tab === "workflow" && (
        <div>
          {/* Stage list — full width; click a stage to open its details + upload in a popup */}
          <div className="space-y-3">
            {(() => {
              const card = (s, i, compact) => (
                <button key={s.key + i}
                  onClick={() => isAccountsStage(s) ? navigate(`/orders/${id}/costing`) : setSel(i)}
                  title={isAccountsStage(s) ? "Click to open the Account Section / Costing sheet" : "Click to open this stage's details, assign a responsible person, and upload its document"}
                  data-testid={`stage-${i}`}
                  className={`w-full text-left border rounded-sm ${compact ? "p-3" : "p-4"} transition-colors ${
                    i === selIdx
                      ? "border-emerald-600/70 bg-emerald-600/5"
                      : stageColor(s) === "green"
                        ? "border-emerald-600/40 bg-emerald-500/[0.04] hover:border-emerald-500/60"
                        : stageColor(s) === "red" || (s.po_place_alert && s.status !== "completed")
                          ? "border-red-600/50 bg-red-500/[0.05] hover:border-red-500/70"
                          : "border-yellow-600/40 bg-yellow-500/[0.04] hover:border-yellow-500/60"
                  }`}>
                  <div className="flex items-start gap-3">
                    {stageColor(s) === "green"
                      ? <CheckCircle size={compact ? 20 : 24} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
                      : stageColor(s) === "red" || (s.po_place_alert && s.status !== "completed")
                        ? <span className={`${compact ? "w-[20px] h-[20px]" : "w-[24px] h-[24px]"} rounded-full border-2 border-red-500 bg-red-500/20 flex items-center justify-center shrink-0 mt-0.5`}><span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" /></span>
                        : s.status === "active"
                          ? <span className={`${compact ? "w-[20px] h-[20px]" : "w-[24px] h-[24px]"} rounded-full border-2 border-yellow-500 bg-yellow-500/20 flex items-center justify-center shrink-0 mt-0.5`}><span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" /></span>
                          : <span className={`${compact ? "w-[20px] h-[20px]" : "w-[24px] h-[24px]"} rounded-full border-2 border-yellow-600/60 shrink-0 mt-0.5`} />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className={`${compact ? "text-xs" : "text-sm"} font-semibold text-zinc-50 truncate`}>{s.name}</div>
                          <div className="text-xs text-zinc-400 mt-0.5 truncate">{s.department || "—"} · {s.assignee_name || "Unassigned"}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <StageBadge s={s} />
                          {s.overdue_days > 0 && <div className="text-[11px] text-red-400 mt-1.5" data-testid={`stage-overdue-${i}`}>{s.overdue_days} day{s.overdue_days > 1 ? "s" : ""} overdue</div>}
                        </div>
                      </div>
                      <Bar pct={stagePct(s)} />
                      {s.po_place_alert && s.status !== "completed" && (
                        <div className="flex items-center gap-2 mt-1.5" data-testid={`stage-place-po-alert-${i}`}>
                          <span className="flex items-center gap-1.5 text-[10px] font-bold text-red-400 uppercase tracking-wide">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shrink-0" /> Place PO — action needed
                          </span>
                          <span onClick={(e) => { e.stopPropagation(); e.preventDefault(); setAutoSendPoNo(s.po_gate_po_no); setSel(i); }}
                            className="text-[10px] font-bold text-zinc-950 bg-red-500 hover:bg-red-400 rounded-full px-2 py-0.5 transition-colors" data-testid={`stage-place-po-now-${i}`}>
                            Place PO Now →
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2 mt-1.5 text-[10px] font-mono text-zinc-500">
                        <span className="text-emerald-400 font-semibold">{stagePct(s)}% complete</span>
                        <span className="truncate">
                          {s.assigned_at ? `Assigned ${fmtDate(s.assigned_at)}` : "Not assigned"}
                          {s.started_at ? ` · Started ${fmtDate(s.started_at)}` : ""}
                          {s.completed_at ? ` · Done ${fmtDate(s.completed_at)}` : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
              const blocks = [];
              for (let i = 0; i < stages.length; i++) {
                if (stages[i].parallel) {
                  const grp = [i];
                  // Stop the group at the Manufacturing boundary too — a run of Drawing/Procurement
                  // "parallel" stages (Design & Nesting / Store / Follow-up) sits immediately
                  // before the Manufacturing "pipeline" stages, and each needs its own block
                  // (and label) even though both sets have parallel:true. Everything WITHIN the
                  // non-Manufacturing dispatch trio still groups together as before.
                  const isMfg = (s) => s.category === "Manufacturing";
                  while (i + 1 < stages.length && stages[i + 1].parallel && isMfg(stages[i + 1]) === isMfg(stages[i])) grp.push(++i);
                  blocks.push({ parallel: true, idxs: grp });
                } else blocks.push({ parallel: false, idxs: [i] });
              }
              return blocks.map((b, bi) => b.parallel ? (
                <div key={`blk-${bi}`} className="border border-emerald-600/30 bg-emerald-600/[0.03] rounded-sm p-3" data-testid={`parallel-block-${bi}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">
                      {stages[b.idxs[0]].category === "Manufacturing" ? "Shop floor pipeline — each station capped by the one before it" : "Parallel streams — run together"}
                    </span>
                    <span className="text-[10px] text-zinc-500 font-mono">{b.idxs.filter((ix) => stages[ix].status === "completed").length}/{b.idxs.length} done</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {b.idxs.map((ix) => card(stages[ix], ix, true))}
                  </div>
                </div>
              ) : card(stages[b.idxs[0]], b.idxs[0], false));
            })()}
          </div>

          {/* Stage detail popup — opens on click; no permanent side panel */}
          <Dialog open={!!S} onOpenChange={(v) => !v && setSel(null)}>
            <DialogContent onPointerDownOutside={(e) => poFullScreen && e.preventDefault()} className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-4xl max-h-[85vh] overflow-y-auto rounded-sm" data-testid="stage-panel">
          {S && (
          <div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">{s_cat(S)}</span>
              <StageBadge s={S} />
            </div>
            <DialogTitle className="font-heading text-xl font-black text-zinc-50 mt-1.5">{S.name}</DialogTitle>
            <DialogDescription className="text-xs text-zinc-400 mt-1">
              {S.type === "customer_approval" ? "Get customer approval on drawings and record the outcome."
                : S.type === "material_check" ? "Verify material availability / receipt and update the status."
                : uploadStageInfo ? "Status auto-tracks how many required documents are uploaded — no manual feedback needed."
                : "Complete this responsibility and submit the required progress feedback."}
            </DialogDescription>

            {/* Pre-Placement 3-Check Gate — server-computed (see po_gate_missing in enrich_order),
                shown as live, tickable checks right here on whichever stage dialog it's relevant
                to (Upload PO for Vendor, Drawing Approval, or Take Confirmation From Store & Place
                PO) — not just a read-only summary pointing elsewhere. */}
            {S.po_gate_po_no && (() => {
              const missing = S.po_gate_missing || [];
              const gate = poGate && poGate !== "fallback" ? poGate : {
                drawing_ok: !missing.includes("Customer Drawing Approval"),
                costing_ok: !missing.includes("Costing Recheck (PO vs Drawing)"),
                store_ok: !missing.includes("Store Stock Check"),
                ready: missing.length === 0, override: null, live: false,
              };
              return (
              <div className={`mt-3 border rounded-sm px-3 py-2.5 ${gate.ready ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"}`} data-testid="stage-po-gate-summary">
                <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-1.5">Pre-Placement Checks — PO {S.po_gate_po_no}</div>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-xs text-zinc-300">
                    <Checkbox checked={gate.drawing_ok} disabled data-testid="stage-gate-drawing-check" />
                    1. Customer Drawing Approval <span className="text-zinc-500">(auto-derived)</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs text-zinc-300">
                    <Checkbox checked={gate.costing_ok} disabled={!canGateCheck || poGateBusy || poGate === "fallback"}
                      onCheckedChange={(v) => togglePoGateCheck("costing_recheck", !!v)} data-testid="stage-gate-costing-check" />
                    2. Costing Recheck (PO vs Drawing)
                  </label>
                  {gate.costing_ok && gate.costing_audit?.by && (
                    <p className="text-[10px] text-emerald-400 pl-6 -mt-1" data-testid="stage-gate-costing-audit">Checked by {gate.costing_audit.by}{gate.costing_audit.at ? ` on ${fmtDate(gate.costing_audit.at)}` : ""}</p>
                  )}
                  <label className="flex items-center gap-2 text-xs text-zinc-300">
                    <Checkbox checked={gate.store_ok} disabled={!canGateCheck || poGateBusy || poGate === "fallback"}
                      onCheckedChange={(v) => togglePoGateCheck("store_check", !!v)} data-testid="stage-gate-store-check" />
                    3. Store Stock Check
                  </label>
                  {gate.store_ok && gate.store_audit?.by && (
                    <p className="text-[10px] text-emerald-400 pl-6 -mt-1" data-testid="stage-gate-store-audit">Checked by {gate.store_audit.by}{gate.store_audit.at ? ` on ${fmtDate(gate.store_audit.at)}` : ""}</p>
                  )}
                  {gate.override && (
                    <p className="text-[10px] text-amber-400 pt-0.5">Overridden by {gate.override.by} on {fmtDate(gate.override.at)}</p>
                  )}
                  {gate.ready ? (
                    <p className="text-xs text-emerald-400 font-semibold flex items-center gap-1.5 pt-1"><CheckCircle size={14} weight="fill" /> All 3 checks cleared — this PO is ready to be placed with the vendor.</p>
                  ) : (
                    <p className="text-[10px] text-zinc-500 pt-0.5">
                      {canGateCheck && poGate !== "fallback" ? "Tick checks 2 & 3 as they're confirmed." : "Only an Admin/Manager or a team member with costing access can tick checks 2 & 3."}
                    </p>
                  )}
                </div>
              </div>
              );
            })()}

            <div className="grid grid-cols-2 sm:grid-cols-5 divide-x divide-y sm:divide-y-0 divide-zinc-800/60 border border-zinc-800 bg-zinc-950/60 rounded-sm mt-4">
              {[["Order Date", o.created_at ? fmtDate(o.created_at) : "—"], ["Due", S.due_at ? fmtDate(S.due_at) : "—"],
                ["Time Taken", S.time_spent ? `${S.started_at_estimated ? "~" : ""}${S.time_spent}` : "—"], ["Progress", uploadStageInfo ? `${uploadStageInfo.pct}%` : `${stagePct(S)}%`],
                ["Department", S.department || "—"]].map(([k, v]) => (
                <div key={k} className="px-3 py-2.5">
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">{k}</div>
                  <div className="text-xs text-zinc-100 font-semibold mt-0.5 truncate">{v}</div>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <div className="text-xs font-bold text-zinc-300 mb-2">Responsible person</div>
              <div className="flex items-center gap-3 border border-zinc-800 bg-zinc-950/60 rounded-sm px-3 py-2.5">
                <span className="w-9 h-9 rounded-sm bg-emerald-600/15 border border-emerald-600/30 flex items-center justify-center text-emerald-400 text-sm font-bold shrink-0">
                  {(S.assignee_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </span>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 font-semibold truncate">{S.assignee_name || "Unassigned"}</div>
                  <div className="text-xs text-zinc-500">{S.department || "—"}{S.type !== "normal" ? ` · ${TYPE_LABEL[S.type]}` : ""}</div>
                </div>
                {S.task_code && <button onClick={() => navigate(`/tasks/${S.task_id}`)} className="ml-auto text-xs font-mono text-emerald-400 hover:underline shrink-0">View Task →</button>}
              </div>
              {isManager && (
                <div className="mt-2">
                  <Select value="" onValueChange={(v) => act(() => api.post(`/orders/${id}/stage/${selIdx}/assign`, { user_id: v }), `${S.assignee_name ? "Reassigned" : "Assigned"} to this stage`)}>
                    <SelectTrigger className={`${inputCls} h-8 text-xs`} data-testid="stage-assign">
                      <SelectValue placeholder={S.assignee_name ? "Reassign this stage to…" : "Assign this stage to…"} />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {!isPoStage(S) && docTypes.length > 0 && (() => {
              const guessed = guessDocType(S.name, docTypes);
              const activeCat = guessed?.name;
              const catFiles = activeCat ? (o.attachments || []).filter((a) => a.category === activeCat) : [];
              const drawingPanels = activeCat && isDrawingCat(activeCat) ? panels : [];
              return (
                <div className="mt-4">
                  <div className="text-xs font-bold text-zinc-300 mb-2">{activeCat ? `${activeCat} for this stage` : "Document upload"}</div>
                  {activeCat ? (
                    drawingPanels.length > 0 ? (
                      <div className="space-y-2" data-testid="stage-panel-drawings">
                        <p className="text-[11px] text-zinc-500 mb-1">One {activeCat.toLowerCase()} slot per panel from the Account Section — {drawingPanels.length} panel{drawingPanels.length > 1 ? "s" : ""} on this job.</p>
                        {drawingPanels.map((p) => {
                          const pFiles = catFiles.filter((a) => a.panel_index === p.index);
                          return (
                            <div key={p.index} className="border border-zinc-800 bg-zinc-950/60 rounded-sm px-3 py-2" data-testid={`panel-drawing-${p.index}`}>
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-xs font-semibold text-zinc-100 truncate">Panel {p.index + 1} — {p.name}</span>
                                {pFiles.length === 0 && (
                                  <label className="text-[11px] font-semibold text-emerald-400 hover:underline cursor-pointer flex items-center gap-1 shrink-0" data-testid={`panel-upload-${p.index}`}>
                                    <Plus size={12} /> Upload
                                    <input type="file" className="hidden" onChange={(e) => uploadFor(activeCat, e, p.index, p.name)} />
                                  </label>
                                )}
                              </div>
                              {pFiles.length === 0 ? (
                                <p className="text-[11px] text-zinc-500">No {activeCat.toLowerCase()} uploaded yet for this panel.</p>
                              ) : pFiles.map((a) => {
                                const canReview = isManager || (a.requested_reviewers || []).includes(user.id);
                                return (
                                <div key={a.id} className="flex items-center gap-2 text-xs border-t border-zinc-800/60 pt-1.5 mt-1.5 flex-wrap" data-testid={`panel-file-${a.id}`}>
                                  <Paperclip size={12} className="text-zinc-500 shrink-0" />
                                  <span className="truncate flex-1 text-zinc-200">{a.name}</span>
                                  {a.approval_status === "approved" ? (
                                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 shrink-0">✓ Approved</span>
                                  ) : a.approval_status === "rejected" ? (
                                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/30 shrink-0">✗ Rejected</span>
                                  ) : (
                                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/40 shrink-0">Pending review</span>
                                  )}
                                  <span className="text-[10px] text-zinc-500 shrink-0">{a.uploaded_by} · {fmtDate(a.uploaded_at)}</span>
                                  <button onClick={() => viewFile(a)} className="text-emerald-400 hover:underline shrink-0" data-testid={`panel-view-${a.id}`}>View</button>
                                  <label className="text-amber-400 hover:underline shrink-0 cursor-pointer" data-testid={`panel-revise-${a.id}`}>
                                    Revise
                                    <input type="file" className="hidden" onChange={(e) => reviseFor(activeCat, e, p.index, p.name, a.id)} />
                                  </label>
                                  <button onClick={() => delAtt(a.id)} className="text-red-400 hover:underline shrink-0" data-testid={`panel-del-${a.id}`}>Delete</button>
                                  {isManager && (
                                    <button onClick={() => { setSelectedDrawings([a.id]); setCheckReqOpen(true); }} className="text-zinc-400 hover:underline shrink-0" data-testid={`panel-check-${a.id}`}>Tag colleague to check</button>
                                  )}
                                  {canReview && a.approval_status !== "approved" && <button onClick={() => reviewDoc(a, "approved")} className="text-emerald-400 hover:underline shrink-0 font-semibold" data-testid={`panel-approve-${a.id}`}>Approve</button>}
                                  {canReview && a.approval_status !== "rejected" && <button onClick={() => reviewDoc(a, "rejected")} className="text-red-400 hover:underline shrink-0 font-semibold" data-testid={`panel-reject-${a.id}`}>Reject</button>}
                                </div>
                                );
                              })}
                            </div>
                          );
                        })}
                        {isApprovedDrawingsStage(S) && (() => {
                          const combinedFiles = catFiles.filter((a) => a.panel_index === null || a.panel_index === undefined);
                          return (
                            <div className="border border-dashed border-zinc-700 rounded-sm px-3 py-2 mt-2" data-testid="approved-drawings-combined">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] text-zinc-400">Or upload one combined PDF covering every panel</span>
                                <label className="text-[11px] font-semibold text-emerald-400 hover:underline cursor-pointer flex items-center gap-1 shrink-0" data-testid="approved-drawings-combined-upload">
                                  <Plus size={12} /> Upload combined PDF
                                  <input type="file" className="hidden" onChange={(e) => uploadFor(activeCat, e)} />
                                </label>
                              </div>
                              {combinedFiles.length > 0 && (
                                <div className="mt-1.5 space-y-1">
                                  {combinedFiles.map((a) => (
                                    <div key={a.id} className="flex items-center gap-2 text-xs border-t border-zinc-800/60 pt-1.5" data-testid={`approved-drawings-combined-file-${a.id}`}>
                                      <Paperclip size={12} className="text-zinc-500 shrink-0" />
                                      <span className="truncate flex-1 text-zinc-200">{a.name}</span>
                                      <span className="text-[10px] text-zinc-500 shrink-0">{a.uploaded_by} · {fmtDate(a.uploaded_at)}</span>
                                      <button onClick={() => viewFile(a)} className="text-emerald-400 hover:underline shrink-0" data-testid={`approved-drawings-combined-view-${a.id}`}>View</button>
                                      <button onClick={() => delAtt(a.id)} className="text-red-400 hover:underline shrink-0" data-testid={`approved-drawings-combined-del-${a.id}`}>Delete</button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      <>
                        <label className="h-9 px-3 inline-flex items-center gap-1.5 border border-emerald-600/40 bg-emerald-600/10 text-emerald-400 text-xs font-semibold rounded-sm cursor-pointer hover:bg-emerald-600/20" data-testid="stage-upload-btn">
                          <Plus size={14} /> Upload {activeCat} <span className="text-[10px] text-emerald-400/70">(multiple allowed)</span>
                          <input type="file" multiple className="hidden" onChange={(e) => uploadFor(activeCat, e)} />
                        </label>
                        {isDrawingCat(activeCat) && (
                          <p className="text-[11px] text-amber-400/80 mt-1.5" data-testid="stage-drawing-no-panels">Add named panel line items in this order's Account Section / Costing sheet to get one upload slot per panel here.</p>
                        )}
                        {/* Every file already uploaded here — also shows up under the same card in the
                            "Document uploads" strip above the tabs, since they share the same category. */}
                        <div className="mt-2 space-y-1.5" data-testid="stage-upload-files">
                          {catFiles.length === 0 ? (
                            <p className="text-[11px] text-zinc-500">No {activeCat} uploaded yet for this job.</p>
                          ) : catFiles.map((a) => {
                            const canReview = isManager || (a.requested_reviewers || []).includes(user.id);
                            return (
                            <div key={a.id} className="flex items-center gap-2 text-xs border border-zinc-800 bg-zinc-950/60 rounded-sm px-2.5 py-1.5 flex-wrap" data-testid={`stage-upload-file-${a.id}`}>
                              <Paperclip size={13} className="text-zinc-500 shrink-0" />
                              <span className="truncate flex-1 text-zinc-200">{a.name}</span>
                              {a.approval_status === "approved" ? (
                                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 shrink-0">✓ Approved</span>
                              ) : a.approval_status === "rejected" ? (
                                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/30 shrink-0">✗ Rejected</span>
                              ) : null}
                              <span className="text-[10px] text-zinc-500 shrink-0">{a.uploaded_by} · {fmtDate(a.uploaded_at)}</span>
                              <button onClick={() => viewFile(a)} className="text-emerald-400 hover:underline shrink-0" data-testid={`stage-upload-view-${a.id}`}>View</button>
                              <button onClick={() => delAtt(a.id)} className="text-red-400 hover:underline shrink-0" data-testid={`stage-upload-del-${a.id}`}>Delete</button>
                              {isManager && (
                                <button onClick={() => { setSelectedDrawings([a.id]); setCheckReqOpen(true); }} className="text-zinc-400 hover:underline shrink-0" data-testid={`stage-upload-check-${a.id}`}>Tag colleague to check</button>
                              )}
                              {canReview && a.approval_status !== "approved" && <button onClick={() => reviewDoc(a, "approved")} className="text-emerald-400 hover:underline shrink-0 font-semibold" data-testid={`stage-upload-approve-${a.id}`}>Approve</button>}
                              {canReview && a.approval_status !== "rejected" && <button onClick={() => reviewDoc(a, "rejected")} className="text-red-400 hover:underline shrink-0 font-semibold" data-testid={`stage-upload-reject-${a.id}`}>Reject</button>}
                            </div>
                            );
                          })}
                        </div>
                      </>
                    )
                  ) : (
                    <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm px-3 py-2.5 text-xs text-amber-300" data-testid="stage-upload-no-folder">
                      No document folder is set up for "{S.name}" yet. Ask an Admin to add a matching Document Type in Masters, or{" "}
                      <label className="underline cursor-pointer font-semibold" data-testid="stage-upload-misc">
                        upload it under Miscellaneous Document for now
                        <input type="file" multiple className="hidden" onChange={(e) => uploadFor("Miscellaneous Document", e)} />
                      </label>.
                    </div>
                  )}
                </div>
              );
            })()}

            {S.task_id && (
              <div className="mt-4 space-y-2" data-testid="stage-team-tools">
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setRemOpen(true)} data-testid="open-reminders" className="border-emerald-600/30 text-emerald-400 bg-emerald-600/5 rounded-sm text-xs">⏰ Reminders</Button>
                </div>
                <div className="flex items-center gap-2">
                  <Input value={subTitle} onChange={(e) => setSubTitle(e.target.value)} placeholder="Sub-task title" className={`${inputCls} h-8 flex-1 text-xs`} data-testid="sub-title" />
                  <Select value={subAssignee} onValueChange={setSubAssignee}>
                    <SelectTrigger className={`${inputCls} h-8 w-32 text-xs`} data-testid="sub-assignee"><SelectValue placeholder="Member" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={addSubTask} data-testid="sub-add" className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-sm text-xs">+ Sub-task</Button>
                </div>
                {/* Every sub-task handed off from this stage — with a one-click nudge so a helper
                    who's gone quiet gets an email/WhatsApp reminder right away. */}
                {(S.children || []).length > 0 && (
                  <div className="space-y-1.5" data-testid="stage-subtasks">
                    {S.children.map((c) => (
                      <div key={c.id} className="flex items-center gap-2 text-xs border border-zinc-800 bg-zinc-950/60 rounded-sm px-2.5 py-1.5" data-testid={`subtask-${c.id}`}>
                        <button onClick={() => navigate(`/tasks/${c.id}`)} className="font-mono text-emerald-400 hover:underline shrink-0">Open →</button>
                        <span className="truncate flex-1 text-zinc-200">{c.title}</span>
                        <span className="text-zinc-500 shrink-0">{c.assignee || "Unassigned"}</span>
                        <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${c.status === "done" ? "bg-emerald-500/15 text-emerald-400" : c.status === "in_progress" ? "bg-emerald-600/15 text-emerald-400" : "bg-zinc-800 text-zinc-500"}`}>{(c.status || "pending").replace("_", " ")}</span>
                        {c.status !== "done" && (
                          <button onClick={() => nudgeSubTask(c)} title="Send an immediate email/WhatsApp reminder" className="text-emerald-400 hover:underline shrink-0" data-testid={`subtask-remind-${c.id}`}>Remind</button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {S.status === "active" && S.key === "dispatch_design" && isManager && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={() => setSkipReasonOpen(true)} data-testid="skip-design-btn" className="bg-amber-600 hover:bg-amber-700 rounded-sm text-xs gap-1"><FlowArrow size={14} /> Skip — Direct to Cutting</Button>
                <span className="text-xs text-zinc-500">This order doesn't need Design/Nesting — sheet goes straight to the cutting machine.</span>
              </div>
            )}
            {S.design_skipped && (
              <div className="mt-3 text-xs text-amber-400 bg-amber-500/5 border border-amber-500/20 rounded-sm px-2.5 py-1.5" data-testid="design-skipped-badge">
                Design/Nesting was skipped for this order — sent direct to Cutting.
                {S.design_skip_reason && <div className="text-zinc-400 mt-1">Reason: <span className="text-zinc-200">{S.design_skip_reason}</span>{S.design_skip_by && ` — by ${S.design_skip_by}`}{S.design_skip_at && ` (${fmtDate(S.design_skip_at)})`}</div>}
              </div>
            )}

            {/* stage actions */}
            {S.status === "active" && S.type === "customer_approval" && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/orders/${id}/remind-customer`), "Reminder sent to customer")} data-testid="remind-customer" className="border-emerald-500/30 text-emerald-400 bg-emerald-500/5 rounded-sm text-xs gap-1"><WhatsappLogo size={14} /> Send Reminder</Button>
                {canApproveDrawings && <Button size="sm" onClick={() => act(() => api.post(`/orders/${id}/customer-approval`), "Approved — advanced")} data-testid="mark-approved" className="bg-emerald-600 hover:bg-emerald-700 rounded-sm text-xs gap-1"><Check size={14} weight="bold" /> Mark Approved</Button>}
                <Button size="sm" variant="outline" onClick={() => setShowRev(true)} data-testid="request-revision" className="border-amber-500/30 text-amber-400 bg-amber-500/5 rounded-sm text-xs gap-1"><ArrowUUpLeft size={14} /> Request Revision</Button>
                <span className="text-xs text-zinc-500">Reminders sent: {S.reminder_count || 0}</span>
              </div>
            )}
            {S.status === "active" && S.type === "material_check" && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={() => act(() => api.post(`/orders/${id}/stage/${selIdx}/material`, { status: "received" }), "Material received")} data-testid="mat-received" className="bg-emerald-600 hover:bg-emerald-700 rounded-sm text-xs gap-1"><Check size={14} /> Received</Button>
                <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/orders/${id}/stage/${selIdx}/material`, { status: "not_received" }), "PO person intimated")} data-testid="mat-not" className="border-red-500/30 text-red-400 bg-red-500/5 rounded-sm text-xs gap-1"><X size={14} /> Not Received</Button>
                <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/orders/${id}/stage/${selIdx}/material`, { status: "partial" }), "Marked partial")} className="border-zinc-800 bg-zinc-950 rounded-sm text-xs">Partial</Button>
                {S.material_status && <span className="text-xs text-zinc-400">Status: <span className="text-zinc-200 capitalize">{S.material_status.replace(/_/g, " ")}</span></span>}
              </div>
            )}

            {isPoStage(S) && (
              <div className="mt-4 border border-emerald-600/30 bg-emerald-600/5 rounded-sm p-3" data-testid="po-stage-panel">
                <div className="text-xs font-bold text-emerald-400 flex items-center gap-1.5 mb-1"><Receipt size={14} /> Purchase Orders for this job</div>
                {canManagePO || hasSpecialPermission(user, "store_receipts") ? (
                  <>
                    <p className="text-xs text-zinc-400 mb-2">Create, edit/revise, attach, and send Purchase Orders to the vendor — right here. Store users can record received quantities and balance material without seeing or changing the commercial workflow.</p>
                    <div className="bg-white text-slate-900 rounded-sm p-3 -mx-1">
                      <OrderPurchaseOrders order={o} canManage={canManagePO}
                        canCreatePurchaseOrders={canCreatePurchaseOrders}
                        canReceive={hasSpecialPermission(user, "store_receipts")}
                        autoOpenPoNo={S.po_gate_po_no === autoSendPoNo ? autoSendPoNo : null}
                        onAutoOpened={() => setAutoSendPoNo(null)}
                        onFullScreenChange={setPoFullScreen} />
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-zinc-500">Only this stage's assigned member, the job creator, backup PO owners, Admin/Manager or the Costing team can manage Purchase Orders here.</p>
                )}
                <div className="mt-3 pt-3 border-t border-emerald-600/20">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">Job Responsibility — can also manage this job's Purchase Orders</div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {(o.job_responsibility_names || []).length ? (o.job_responsibility_names || []).map((nm, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-zinc-800 border border-zinc-700 rounded-full px-2 py-0.5 text-zinc-200" data-testid={`po-owner-chip-${i}`}>{nm}</span>
                    )) : <span className="text-[11px] text-zinc-600">None assigned</span>}
                  </div>
                  <button type="button" onClick={() => document.querySelector('[data-testid="job-resp-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" })} className="text-[11px] text-emerald-400 hover:underline mt-1.5">Manage from the Job Responsibility card above ↑</button>
                </div>
              </div>
            )}

            {(S.children || []).length > 0 && (
              <div className="mt-4 border-l-2 border-zinc-800 pl-3 space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Sub-department tasks</div>
                {S.children.map((c) => (
                  <button key={c.id} onClick={() => navigate(`/tasks/${c.id}`)} data-testid={`substage-${c.id}`} className="w-full flex items-center gap-2 py-1 text-left hover:text-emerald-400 transition-colors">
                    <span className="text-xs text-zinc-300 flex-1 truncate">{(c.title || "").split(" — ")[0]}</span>
                    <span className="text-[10px] text-zinc-500">{c.assignee || "Unassigned"}</span>
                    <StatusBadge status={c.status} />
                  </button>
                ))}
              </div>
            )}

            {/* Progress feedback — hidden for upload-driven stages (status auto-tracks uploads) and for
                Customer Approval stages, which are a binary Approved/Not-Approved outcome, not a %. Letting
                someone self-report a % here (capped at 99 until really completed) made the stage card sit at
                "99% / yellow" even though nothing was actually pending beyond clicking Mark Approved. */}
            {!uploadStageInfo && S.type !== "customer_approval" && (
            <div className="mt-5 pt-4 border-t border-zinc-800">
              <div className="text-xs font-bold text-zinc-300 mb-2">Mandatory progress feedback</div>
              {mfgCap && (
                <div className={`text-[11px] rounded-sm px-2.5 py-1.5 mb-2 border ${mfgCap.overridden ? "text-purple-300 bg-purple-500/5 border-purple-500/20" : "text-amber-400 bg-amber-500/5 border-amber-500/20"}`} data-testid="mfg-cap-banner">
                  {mfgCap.overridden
                    ? <>Cap manually overridden to {o.total_units ? `${mfgCap.units} of ${o.total_units} units` : `${mfgCap.pct}%`} by {mfgCap.by} — {mfgCap.reason}</>
                    : o.total_units
                      ? <>Capped at {mfgCap.units} of {o.total_units} units — "{mfgCap.prevName}" has only cleared {mfgCap.units} so far.</>
                      : <>Capped at {mfgCap.pct}% — "{mfgCap.prevName}" has only cleared {mfgCap.pct}% of the material so far.</>}
                </div>
              )}
              {user.role === "admin" && S.category === "Manufacturing" && selIdx > 0 && stages[selIdx - 1]?.category === "Manufacturing" && (
                <div className="mb-2">
                  {mfgCap?.overridden ? (
                    <button onClick={clearCapOverride} data-testid="clear-cap-override-btn" className="text-[11px] text-purple-300 hover:underline">Clear cap override</button>
                  ) : capOverrideOpen ? (
                    <div className="flex flex-wrap items-center gap-1.5 border border-purple-500/20 bg-purple-500/[0.03] rounded-sm p-2">
                      <Input type="number" min="0" placeholder={o.total_units ? "Units" : "%"} value={capOverrideVal} onChange={(e) => setCapOverrideVal(e.target.value)} className={`${inputCls} h-8 w-20`} data-testid="cap-override-value" />
                      <Input placeholder="Reason (required)" value={capOverrideReason} onChange={(e) => setCapOverrideReason(e.target.value)} className={`${inputCls} h-8 flex-1 min-w-[140px]`} data-testid="cap-override-reason" />
                      <Button size="sm" onClick={setCapOverride} data-testid="cap-override-submit" className="bg-purple-500 hover:bg-purple-600 text-zinc-950 rounded-sm text-xs font-semibold h-8">Set</Button>
                      <button onClick={() => setCapOverrideOpen(false)} className="text-[11px] text-zinc-500 hover:underline">Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => setCapOverrideOpen(true)} data-testid="open-cap-override-btn" className="text-[11px] text-purple-300 hover:underline">Override cap (rework / edge case)</button>
                  )}
                </div>
              )}
              <div className="space-y-2">
                {o.total_units && S.category === "Manufacturing" ? (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1">Units cleared (of {o.total_units}){mfgCap ? ` — max ${mfgCap.units}` : ""}</label>
                    <Input type="number" min="0" max={mfgCap ? mfgCap.units : o.total_units} value={fb.units_cleared} onChange={(e) => setFb({ ...fb, units_cleared: e.target.value })} placeholder={`${S.units_cleared || 0}`} className={`${inputCls} h-9`} data-testid="fb-units-cleared" />
                  </div>
                ) : (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1">Progress percentage{mfgCap ? ` (max ${Math.min(mfgCap.pct, 99)}%)` : " (max 99% — reaches 100% only once the stage is actually completed)"}</label>
                    <Input type="number" min="0" max={mfgCap ? Math.min(mfgCap.pct, 99) : 99} value={fb.progress} onChange={(e) => setFb({ ...fb, progress: e.target.value })} placeholder={`${stagePct(S)}`} className={`${inputCls} h-9`} data-testid="fb-progress" />
                  </div>
                )}
                <Textarea rows={2} value={fb.work_completed} onChange={(e) => setFb({ ...fb, work_completed: e.target.value })} placeholder="Work completed" className={inputCls} data-testid="fb-completed" />
                <Textarea rows={2} value={fb.work_pending} onChange={(e) => setFb({ ...fb, work_pending: e.target.value })} placeholder="Work pending / next action" className={inputCls} data-testid="fb-pending" />
                <Textarea rows={2} value={fb.blocker} onChange={(e) => setFb({ ...fb, blocker: e.target.value })} placeholder="Problem, blocker or support required" className={inputCls} data-testid="fb-blocker" />
                <div className="flex items-center justify-between gap-2">
                  <button onClick={() => logDelay(selIdx)} data-testid={`log-delay-${selIdx}`} className="text-[11px] text-amber-400 hover:underline">+ Log delay</button>
                  <div className="flex items-center gap-2">
                    {S.category === "Manufacturing" && isManager && S.status === "active" && (
                      <Button size="sm" variant="outline" onClick={completeMfgStage} disabled={mfgCap && mfgCap.pct < 100} data-testid="mfg-complete-stage-btn" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-400 rounded-sm text-xs font-semibold">Mark Stage Complete</Button>
                    )}
                    <Button size="sm" onClick={submitFb} data-testid="fb-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs font-semibold">Submit Feedback</Button>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Progress History — one merged mini timeline of every self-reported update AND
                every logged delay on this stage (not just the latest), newest first. */}
            {((S.feedback || []).length > 0 || (S.delays || []).length > 0 || S.time_spent) && (() => {
              const items = [
                ...(S.feedback || []).map((f) => ({ kind: "feedback", at: f.at, key: f.id, data: f })),
                ...(S.delays || []).map((d, di) => ({ kind: "delay", at: d.at, key: `delay-${di}`, data: d })),
              ].sort((a, b) => new Date(b.at) - new Date(a.at));
              return (
                <div className="mt-4 pt-3 border-t border-zinc-800">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold text-zinc-300 flex items-center gap-1.5"><ClockCounterClockwise size={13} className="text-emerald-400" /> Progress History</div>
                    {items.length > 0 && <span className="text-[10px] font-mono text-zinc-500" data-testid="fb-history-count">{items.length} update{items.length > 1 ? "s" : ""}</span>}
                  </div>
                  {S.time_spent && <div className="text-[11px] font-mono text-zinc-500 mb-2">⏱ Time on stage: {S.time_spent}</div>}
                  <div className="relative pl-4 space-y-3" data-testid="fb-history-timeline">
                    {items.map((it, ii) => (
                      <div key={it.key} className="relative">
                        {ii < items.length - 1 && <span className="absolute -left-4 top-3.5 bottom-[-12px] w-px bg-zinc-800" />}
                        <span className={`absolute -left-[19px] top-1 w-2.5 h-2.5 rounded-full ring-2 ring-zinc-900 ${it.kind === "delay" ? "bg-amber-500" : "bg-emerald-600"}`} />
                        {it.kind === "feedback" ? (
                          <div className="text-xs border border-zinc-800 bg-zinc-950/60 rounded-sm px-3 py-2" data-testid={`fb-entry-${it.data.id}`}>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-zinc-200 font-semibold">{it.data.by} · <span className="text-emerald-400 font-mono">{it.data.progress}%</span></span>
                              <span className="text-[10px] font-mono text-zinc-500">{fmtDate(it.data.at)}</span>
                            </div>
                            {it.data.work_completed && <div className="text-zinc-400"><span className="text-emerald-400">Done:</span> {it.data.work_completed}</div>}
                            {it.data.work_pending && <div className="text-zinc-400"><span className="text-amber-400">Pending:</span> {it.data.work_pending}</div>}
                            {it.data.blocker && <div className="text-red-300 flex items-start gap-1"><WarningCircle size={13} className="mt-0.5 shrink-0" /> {it.data.blocker}</div>}
                          </div>
                        ) : (
                          <div className="text-[11px] text-amber-400/90 bg-amber-500/5 border border-amber-500/20 px-3 py-2 rounded-sm flex items-start gap-1.5" data-testid={`delay-entry-${it.key}`}>
                            <Warning size={13} className="mt-0.5 shrink-0" />
                            <span className="flex-1"><span className="font-semibold">Delay</span> — {it.data.by}: {it.data.reason}</span>
                            <span className="text-zinc-500 font-mono shrink-0">{fmtDate(it.data.at)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
          )}
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ------- DOCUMENTS TAB ------- */}
      {tab === "docs" && (
        <>
        {/* All Drawings — one place to see every drawing across every panel/category, tick a
            few, download them (asks which folder to save into) or raise a check request tagging
            a specific colleague, who then gets Approve/Reject rights on just those files. */}
        {(() => {
          const allDrawings = (o.attachments || []).filter((a) => isDrawingCat(a.category) && !a.superseded);
          if (allDrawings.length === 0) return null;
          const allSelected = allDrawings.length > 0 && selectedDrawings.length === allDrawings.length;
          return (
            <div className="border border-emerald-600/30 bg-emerald-600/5 rounded-sm p-4 mb-4 max-w-5xl" data-testid="all-drawings-section">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-400">All Drawings ({allDrawings.length})</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer">
                    <Checkbox checked={allSelected} onCheckedChange={(v) => setSelectedDrawings(v ? allDrawings.map((a) => a.id) : [])} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="select-all-drawings" /> Select all
                  </label>
                  <Button size="sm" variant="outline" disabled={!selectedDrawings.length} onClick={downloadSelected} data-testid="download-selected-drawings" className="border-zinc-800 bg-zinc-950 text-zinc-300 text-xs h-8 gap-1 disabled:opacity-40">
                    <FileArrowDown size={13} /> Download ({selectedDrawings.length})
                  </Button>
                  <Button size="sm" variant="outline" disabled={!selectedDrawings.length} onClick={() => setCheckReqOpen(true)} data-testid="raise-check-request-btn" className="border-amber-500/30 text-amber-400 bg-amber-500/5 text-xs h-8 gap-1 disabled:opacity-40">
                    <Eye size={13} /> Raise Check Request
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                {allDrawings.map((a) => {
                  const panelLabel = a.panel_index != null ? `Panel ${a.panel_index + 1} — ${a.panel_name || ""}` : "—";
                  const canReview = isManager || (a.requested_reviewers || []).includes(user.id);
                  const reviewerNames = (a.requested_reviewers || []).map((rid) => users.find((u) => u.id === rid)?.name).filter(Boolean);
                  return (
                    <div key={a.id} className="flex items-center gap-2 text-xs border border-zinc-800 bg-zinc-950/60 rounded-sm px-2.5 py-1.5 flex-wrap" data-testid={`drawing-row-${a.id}`}>
                      <Checkbox checked={selectedDrawings.includes(a.id)} onCheckedChange={(v) => setSelectedDrawings((p) => v ? [...p, a.id] : p.filter((x) => x !== a.id))} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid={`select-drawing-${a.id}`} />
                      <span className="text-zinc-500 w-32 truncate shrink-0">{a.category}</span>
                      <span className="text-zinc-400 w-36 truncate shrink-0">{panelLabel}</span>
                      <span className="flex-1 min-w-[100px] truncate text-zinc-200">{a.name}</span>
                      {a.approval_status === "approved" ? (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 shrink-0">✓ Approved</span>
                      ) : a.approval_status === "rejected" ? (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/30 shrink-0">✗ Rejected</span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/40 shrink-0">Pending</span>
                      )}
                      {reviewerNames.length > 0 && <span className="text-[10px] text-amber-400 shrink-0" data-testid={`check-requested-${a.id}`}>check: {reviewerNames.join(", ")}</span>}
                      <button onClick={() => viewFile(a)} className="text-emerald-400 hover:underline shrink-0" data-testid={`drawing-view-${a.id}`}>View</button>
                      {reviewerNames.length > 0 && !["approved", "rejected"].includes(a.approval_status) && (
                        <button onClick={() => act(() => api.post(`/orders/${id}/attachments/${a.id}/remind-reviewers`), "Reminder sent")} title="Send an immediate email/in-app reminder to whoever was tagged" className="text-amber-400 hover:underline shrink-0" data-testid={`drawing-remind-${a.id}`}>Remind</button>
                      )}
                      {canApproveDrawings && canReview && a.approval_status !== "approved" && <button onClick={() => reviewDoc(a, "approved")} className="text-emerald-400 hover:underline shrink-0" data-testid={`drawing-approve-${a.id}`}>Approve</button>}
                      {canApproveDrawings && canReview && a.approval_status !== "rejected" && <button onClick={() => reviewDoc(a, "rejected")} className="text-red-400 hover:underline shrink-0" data-testid={`drawing-reject-${a.id}`}>Reject</button>}
                      {isManager && <button onClick={() => openShare(a)} className="text-sky-400 hover:underline shrink-0" data-testid={`drawing-share-${a.id}`}>Share with Customer</button>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-sm p-5 max-w-3xl" data-testid="doc-strip">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Document Register — organised by folder</div>
          {(() => {
            const byCat = (o.attachments || []).reduce((acc, a) => { const k = a.category || "General"; (acc[k] = acc[k] || []).push(a); return acc; }, {});
            const reqList = o.required_docs || [];
            const typeNames = docTypes.map((d) => d.name);
            // "Costing Sheet" attachments are shown once, under the "Customer Order Details for
            // Account" folder below — excluded here so the same files never appear twice.
            const others = Object.keys(byCat).filter((c) => !typeNames.includes(c) && c !== "Costing Sheet");
            const fileRow = (a, revise) => (
              <div key={a.id} className="py-1">
                <div className="flex items-center gap-2 text-xs">
                  <Paperclip size={11} className="text-zinc-600 shrink-0" />
                  <span className="flex-1 text-zinc-300 truncate">{a.name}</span>
                  {a.approval_status === "approved" ? (
                    <span title={a.approval_remarks || ""} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 shrink-0" data-testid={`doc-appr-${a.id}`}>✓ Approved</span>
                  ) : a.approval_status === "rejected" ? (
                    <span title={a.approval_remarks || ""} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/30 shrink-0" data-testid={`doc-appr-${a.id}`}>✗ Rejected</span>
                  ) : (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/40 shrink-0" data-testid={`doc-appr-${a.id}`}>Pending review</span>
                  )}
                  <span className="text-[10px] text-zinc-600 font-mono shrink-0">{a.uploaded_by} · {fmtDate(a.uploaded_at)} · db-stored</span>
                  {(isManager || (a.requested_reviewers || []).includes(user.id)) && a.approval_status !== "approved" && (
                    <button onClick={() => reviewDoc(a, "approved")} title="Approve document" data-testid={`approve-${a.id}`} className="px-1.5 py-0.5 text-[10px] font-bold text-emerald-400 border border-emerald-500/30 rounded-sm hover:bg-emerald-500/10 transition-colors">✓</button>
                  )}
                  {(isManager || (a.requested_reviewers || []).includes(user.id)) && a.approval_status !== "rejected" && (
                    <button onClick={() => reviewDoc(a, "rejected")} title="Reject document" data-testid={`reject-${a.id}`} className="px-1.5 py-0.5 text-[10px] font-bold text-red-400 border border-red-500/30 rounded-sm hover:bg-red-500/10 transition-colors">✗</button>
                  )}
                  <button onClick={() => viewFile(a)} title="View / Print" data-testid={`view-${a.id}`} className="p-1 text-zinc-400 hover:text-emerald-400 transition-colors"><Eye size={14} /></button>
                  <button onClick={() => downloadFile(a)} title="Save / Download" data-testid={`dl-${a.id}`} className="p-1 text-zinc-400 hover:text-emerald-400 transition-colors"><FileArrowDown size={14} /></button>
                  {isManager && isDrawingCat(a.category) && (
                    <button onClick={() => openShare(a)} title="Share a secure no-login link with the customer" data-testid={`share-${a.id}`} className="p-1 text-sky-400 hover:text-sky-300 transition-colors"><ShareNetwork size={14} /></button>
                  )}
                  {revise && (
                    <label className="p-1 text-amber-400 hover:text-amber-300 transition-colors cursor-pointer" title="Revise — replace with a new version" data-testid={`revise-${a.id}`}>
                      <ArrowUUpLeft size={13} />
                      <input type="file" className="hidden" onChange={(e) => reviseFor(revise.cat, e, revise.panelIdx, revise.panelName, a.id)} />
                    </label>
                  )}
                  <button onClick={() => delAtt(a.id)} title="Delete" data-testid={`delete-${a.id}`} className="p-1 text-zinc-600 hover:text-red-400 transition-colors"><Trash size={13} /></button>
                </div>
                {a.approval_remarks && (
                  <div className="text-[10px] text-zinc-500 italic pl-5" data-testid={`doc-remarks-${a.id}`}>Remarks: {a.approval_remarks} — {a.reviewed_by}</div>
                )}
              </div>
            );
            // One card per Document Type — reused inside whichever folder it's bucketed into below,
            // so every file appears in exactly ONE place instead of a folder strip AND a flat list.
            const docTypeCard = (d) => {
              const allFiles = byCat[d.name] || [];
              const files = allFiles.filter((a) => !a.superseded);
              const required = reqList.includes(d.name);
              const missing = required && files.length === 0;
              const drawingPanels = isDrawingCat(d.name) ? panels : [];
              return (
                <div key={d.id} className={`border rounded-sm ${missing ? "border-red-500/40 bg-red-500/5" : "border-zinc-800 bg-zinc-950/60"}`} data-testid={`doc-slot-${d.name}`}>
                  <div className="flex items-center gap-2 px-3 py-2.5 flex-wrap">
                    <span className="text-sm font-semibold text-zinc-100">{d.name}</span>
                    {required && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-600/15 text-emerald-400 border border-emerald-600/30">Required</span>}
                    <span className={`text-xs ${missing ? "text-red-400 font-semibold" : files.length ? "text-emerald-400" : "text-zinc-500"}`} data-testid={`doc-status-${d.name}`}>
                      {missing ? "Missing — upload now" : files.length ? `✓ ${files.length} file${files.length > 1 ? "s" : ""}` : "No file"}
                    </span>
                    {drawingPanels.length === 0 && (
                      <label className="ml-auto text-xs text-emerald-400 hover:underline cursor-pointer flex items-center gap-1 shrink-0" data-testid={`upload-${d.name}`}>
                        <Paperclip size={12} /> Upload
                        <input type="file" multiple className="hidden" onChange={(e) => uploadFor(d.name, e)} />
                      </label>
                    )}
                  </div>
                  {drawingPanels.length > 0 ? (
                    <div className="border-t border-zinc-800/60 px-3 py-2 space-y-2" data-testid={`doc-panels-${d.name}`}>
                      <p className="text-[10px] text-zinc-500">One slot per Account Section panel — {drawingPanels.length} panel{drawingPanels.length > 1 ? "s" : ""} on this job.</p>
                      {drawingPanels.map((p) => {
                        const pFiles = files.filter((a) => a.panel_index === p.index);
                        const history = allFiles.filter((a) => a.panel_index === p.index && a.superseded)
                          .sort((a, b) => (b.version || 1) - (a.version || 1));
                        const historyKey = `${d.name}-${p.index}`;
                        const historyOpen = !!openHistory[historyKey];
                        return (
                          <div key={p.index} className="border border-zinc-800/60 rounded-sm px-2.5 py-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-semibold text-zinc-300">Panel {p.index + 1} — {p.name}</span>
                              {pFiles.length === 0 && (
                                <label className="text-[11px] text-emerald-400 hover:underline cursor-pointer flex items-center gap-1 shrink-0" data-testid={`doc-panel-upload-${d.name}-${p.index}`}>
                                  <Paperclip size={11} /> Upload
                                  <input type="file" className="hidden" onChange={(e) => uploadFor(d.name, e, p.index, p.name)} />
                                </label>
                              )}
                            </div>
                            {pFiles.length === 0
                              ? <p className="text-[10px] text-zinc-500 mt-0.5">No file uploaded yet for this panel.</p>
                              : pFiles.map((a) => fileRow(a, { cat: d.name, panelIdx: p.index, panelName: p.name }))}
                            {history.length > 0 && (
                              <div className="mt-1.5 pl-2 border-l border-zinc-800">
                                <button type="button" onClick={() => setOpenHistory((s) => ({ ...s, [historyKey]: !s[historyKey] }))} className="text-[10px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1" data-testid={`version-history-toggle-${d.name}-${p.index}`}>
                                  <ClockCounterClockwise size={10} /> {historyOpen ? "Hide" : "Show"} version history ({history.length}) — nothing is ever deleted
                                </button>
                                {historyOpen && (
                                  <div className="mt-1 space-y-1" data-testid={`version-history-list-${d.name}-${p.index}`}>
                                    {history.map((a) => (
                                      <div key={a.id} className="flex items-center gap-2 text-[10px] text-zinc-500" data-testid={`version-row-${a.id}`}>
                                        <span className="flex-1 truncate">v{a.version || 1} — {a.name}</span>
                                        <span className="text-zinc-600 shrink-0">{fmtDate(a.uploaded_at)}</span>
                                        <button onClick={() => viewFile(a)} title="Preview" className="p-0.5 text-zinc-400 hover:text-emerald-400 shrink-0" data-testid={`version-view-${a.id}`}><Eye size={11} /></button>
                                        <button onClick={() => downloadFile(a)} title="Download" className="p-0.5 text-zinc-400 hover:text-emerald-400 shrink-0" data-testid={`version-dl-${a.id}`}><FileArrowDown size={11} /></button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {files.some((a) => a.panel_index == null) && (
                        <div className="border border-dashed border-zinc-700 rounded-sm px-2.5 py-1.5">
                          <span className="text-[10px] text-zinc-500">Uploaded before per-panel tracking was added:</span>
                          {files.filter((a) => a.panel_index == null).map((a) => fileRow(a))}
                        </div>
                      )}
                    </div>
                  ) : (
                    files.length > 0 && <div className="border-t border-zinc-800/60 px-3 py-1.5">{files.map((a) => fileRow(a))}</div>
                  )}
                </div>
              );
            };
            const buckets = bucketDocTypes(docTypes);
            const accountFiles = byCat["Costing Sheet"] || [];
            // The 5 folders requested — every Document Type card renders inside exactly one of
            // these (or the "account" folder, which isn't a Document Type at all, just Costing
            // Sheet attachments), replacing the separate always-visible folder strip that used to
            // sit above these tabs and repeat the same files.
            const folders = [
              { key: "new_order", label: "Start New Order Documents", types: buckets.new_order },
              { key: "account", label: "Customer Order Details for Account", types: [], isAccount: true, files: accountFiles },
              { key: "po_vendor", label: "PO for Vendors", types: buckets.po_vendor },
              { key: "drawings", label: "Drawings", types: buckets.drawings },
              { key: "approved_drawings", label: "Approved Drawings", types: buckets.approved_drawings },
            ];
            return (
              <div className="space-y-2">
                {folders.map((f) => {
                  const fileCount = f.isAccount ? f.files.length : f.types.reduce((n, d) => n + (byCat[d.name] || []).length, 0);
                  const missingReq = f.types.some((d) => reqList.includes(d.name) && !(byCat[d.name] || []).length);
                  const isOpen = openFolder === f.key;
                  return (
                    <div key={f.key} className={`border rounded-sm ${missingReq ? "border-red-500/40 bg-red-500/5" : "border-zinc-800 bg-zinc-900/40"}`} data-testid={`folder-${f.key}`}>
                      <button type="button" onClick={() => setOpenFolder(isOpen ? null : f.key)} className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left" data-testid={`folder-toggle-${f.key}`}>
                        <span className="flex items-center gap-2 min-w-0">
                          {isOpen ? <FolderOpen size={15} weight="fill" className="text-emerald-400 shrink-0" /> : <Folder size={15} weight="fill" className="text-emerald-400/80 shrink-0" />}
                          <span className="text-sm font-semibold text-zinc-100 truncate">{f.label}</span>
                          {missingReq && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" title="A required document is missing in this folder" />}
                        </span>
                        <span className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs text-zinc-500 font-mono">{fileCount}</span>
                          {isOpen ? <CaretDown size={13} className="text-zinc-500" /> : <CaretRight size={13} className="text-zinc-500" />}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="border-t border-zinc-800 px-3 py-2.5 space-y-2" data-testid={`folder-body-${f.key}`}>
                          {f.isAccount ? (
                            <>
                              {f.files.length === 0
                                ? <p className="text-xs text-zinc-500">No Account Section files yet.</p>
                                : f.files.map((a) => fileRow(a))}
                              <button onClick={() => navigate(`/orders/${id}/costing`)} className="text-xs text-emerald-400 hover:underline" data-testid="folder-open-account">Open Account Section →</button>
                            </>
                          ) : f.types.length === 0 ? (
                            <p className="text-xs text-zinc-500">No document types in this folder yet.</p>
                          ) : (
                            f.types.map(docTypeCard)
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {others.map((cat) => (
                  <div key={cat} className="border border-zinc-800 bg-zinc-950/60 rounded-sm" data-testid={`doc-slot-${cat}`}>
                    <div className="flex items-center gap-2 px-3 py-2.5">
                      <span className="text-sm font-semibold text-zinc-100">{cat}</span>
                      <span className="text-xs text-emerald-400">✓ {byCat[cat].length} file{byCat[cat].length > 1 ? "s" : ""}</span>
                      <label className="ml-auto text-xs text-emerald-400 hover:underline cursor-pointer flex items-center gap-1 shrink-0">
                        <Paperclip size={12} /> Upload
                        <input type="file" multiple className="hidden" onChange={(e) => uploadFor(cat, e)} />
                      </label>
                    </div>
                    <div className="border-t border-zinc-800/60 px-3 py-1.5">{byCat[cat].map((a) => fileRow(a))}</div>
                  </div>
                ))}
                <div className="flex items-center justify-between border border-dashed border-zinc-800 rounded-sm px-3 py-2.5">
                  <span className="text-xs text-zinc-500">Any other document (uncategorised)</span>
                  <label className="text-xs text-emerald-400 hover:underline cursor-pointer flex items-center gap-1" data-testid="order-upload">
                    <Paperclip size={12} /> Upload
                    <input type="file" multiple className="hidden" onChange={(e) => uploadFor("General", e)} />
                  </label>
                </div>
                <p className="text-[10px] text-zinc-500">Max file size: 50 MB per file.</p>
              </div>
            );
          })()}
          {(o.details || []).filter((d) => d.title !== "Project Reference").length > 0 && (
            <div className="mt-5 pt-4 border-t border-zinc-800 flex flex-wrap gap-2">
              {o.details.filter((d) => d.title !== "Project Reference").map((d, i) => (
                <span key={i} className="text-xs bg-zinc-950 border border-zinc-800 px-2 py-1 rounded-sm"><span className="text-zinc-500">{d.title}:</span> <span className="text-zinc-200">{d.value}</span></span>
              ))}
            </div>
          )}
        </div>
        </>
      )}
      {tab === "material" && (
        <div className="space-y-5 max-w-4xl">
          <div className="border border-zinc-800 bg-zinc-900/30 rounded-sm p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">PO Builder</div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-400">Vendor</span>
                <Select value={o.po_vendor || ""} onValueChange={setVendor}>
                  <SelectTrigger className={`${inputCls} h-9 w-56`} data-testid="po-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
                    {vendors.length === 0 && <div className="px-2 py-1.5 text-xs text-zinc-500">Add vendors in Masters → Vendors</div>}
                    {vendors.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={previewPO} data-testid="preview-po" className="border-zinc-800 bg-zinc-900 rounded-sm gap-1"><Eye size={16} /> Preview PO</Button>
              <Button onClick={genPO} data-testid="generate-po" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1"><FileArrowDown size={16} /> Generate PO (Excel)</Button>
            </div>
            <div className="mt-4 pt-4 border-t border-zinc-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Line Items</span>
                <div className="flex items-center gap-3">
                  <button onClick={addItem} data-testid="add-po-item" className="text-xs text-emerald-400 hover:text-emerald-300 font-semibold">+ Add Item</button>
                  <button onClick={saveItems} data-testid="save-po-items" className="text-xs text-zinc-300 hover:text-zinc-100 font-semibold">Save Items</button>
                </div>
              </div>
              {poItems.length === 0 ? (
                <div className="text-xs text-zinc-500">No line items. Add items to include a priced table in the PO.</div>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-[1fr_80px_100px_110px_32px] gap-2 text-[10px] uppercase tracking-wider text-zinc-500 px-1">
                    <span>Item</span><span>Qty</span><span>Rate</span><span className="text-right">Amount</span><span></span>
                  </div>
                  {poItems.map((it, i) => (
                    <div key={i} className="grid grid-cols-[1fr_80px_100px_110px_32px] gap-2 items-center" data-testid={`po-item-${i}`}>
                      <Input value={it.desc} onChange={(e) => updItem(i, "desc", e.target.value)} placeholder="Description" className={`${inputCls} h-8`} data-testid={`po-item-desc-${i}`} />
                      <Input type="number" value={it.qty} onChange={(e) => updItem(i, "qty", e.target.value)} className={`${inputCls} h-8`} data-testid={`po-item-qty-${i}`} />
                      <Input type="number" value={it.rate} onChange={(e) => updItem(i, "rate", e.target.value)} className={`${inputCls} h-8`} data-testid={`po-item-rate-${i}`} />
                      <span className="text-right text-sm text-zinc-300 font-mono">{((Number(it.qty) || 0) * (Number(it.rate) || 0)).toLocaleString()}</span>
                      <button onClick={() => delItem(i)} className="text-zinc-600 hover:text-red-400"><X size={14} /></button>
                    </div>
                  ))}
                  <div className="flex items-center justify-end pt-2 border-t border-zinc-800/60">
                    <span className="text-sm font-bold text-zinc-300">Total: <span className="font-mono text-emerald-400" data-testid="po-total">{poTotal.toLocaleString()}</span></span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border border-zinc-800 bg-zinc-900/30 rounded-sm p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Material Checks</div>
            {matStages.length === 0 && <div className="text-sm text-zinc-500">No material-check stages in this workflow.</div>}
            <div className="space-y-2">
              {matStages.map((s) => (
                <div key={s.idx} className="flex items-center gap-3 border border-zinc-800 bg-zinc-950/60 rounded-sm px-3 py-2.5" data-testid={`mat-row-${s.idx}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100 font-semibold truncate">{s.name}</div>
                    <div className="text-xs text-zinc-500">{s.department || "—"} · {s.assignee_name || "Unassigned"}</div>
                  </div>
                  {s.material_status
                    ? <span className={`text-xs capitalize font-semibold ${s.material_status === "received" ? "text-emerald-400" : s.material_status === "partial" ? "text-amber-400" : "text-red-400"}`}>{s.material_status.replace(/_/g, " ")}</span>
                    : <span className="text-xs text-zinc-500">Not recorded</span>}
                  <StageBadge s={s} />
                  <button onClick={() => { setTab("workflow"); setSel(s.idx); }} className="text-xs text-emerald-400 hover:underline">Open →</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ------- COMMUNICATION & AUDIT TAB ------- */}
      {tab === "comms" && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5 items-start max-w-6xl">
          <div className="border border-zinc-800 bg-zinc-900/30 rounded-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Correspondence — customer & vendor</div>
              <Button size="sm" onClick={() => setComposeOpen(true)} data-testid="open-compose-inline" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs gap-1"><PaperPlaneRight size={13} weight="fill" /> Compose</Button>
            </div>
            <div className="space-y-3 max-h-[28rem] overflow-y-auto">
              {(o.messages || []).length === 0 && <div className="text-sm text-zinc-500">No correspondence yet. Every email/WhatsApp sent to the customer or a vendor from this order will be logged here.</div>}
              {[...(o.messages || [])].reverse().map((m) => (
                <div key={m.id} className="flex gap-3" data-testid={`msg-${m.id}`}>
                  <div className="w-8 h-8 rounded-sm bg-zinc-800 flex items-center justify-center text-emerald-400 text-sm font-bold shrink-0">{m.user_name?.[0]}</div>
                  <div className="flex-1 bg-zinc-950 border border-zinc-800 p-3 rounded-sm">
                    <div className="flex justify-between items-center mb-1 gap-2">
                      <span className="text-sm text-zinc-100">{m.user_name}{m.recipient_label ? <span className="text-zinc-500"> → {m.recipient_label}</span> : ""}</span>
                      <span className="text-[10px] font-mono text-zinc-500 shrink-0">{fmtDate(m.at)}</span>
                    </div>
                    {m.subject && <div className="text-xs text-zinc-400 font-semibold mb-1">Subject: {m.subject}</div>}
                    <p className="text-sm text-zinc-300">{m.text}</p>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {(m.channels || []).filter((c) => c !== "in_app").map((c) => <span key={c} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-400">{c}</span>)}
                      {(m.tagged_user_ids || []).length > 0 && <span className="text-[10px] text-emerald-400">tagged {m.tagged_user_ids.map((tid) => users.find((u) => u.id === tid)?.name).filter(Boolean).join(", ")}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-zinc-800 bg-zinc-900/30 rounded-sm p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Delay & Reminder Log</div>
            <div className="text-xs text-zinc-400 mb-3 flex items-center gap-2">
              <span className={o.reminders_enabled ? "text-emerald-400" : "text-zinc-600"}>●</span>
              {o.reminders_enabled ? `Auto reminders via ${(o.reminder_channels || ["email", "whatsapp"]).join(" & ")} every ${o.reminder_value} ${o.reminder_unit} until approved` : "Auto reminders off"}
            </div>
            <div className="space-y-2">
              {totalDelays === 0 && <div className="text-sm text-zinc-500">No delays logged on this order.</div>}
              {stages.flatMap((s, i) => (s.delays || []).map((d, di) => (
                <div key={`${i}-${di}`} className="text-[11px] text-amber-400/90 bg-amber-500/5 border border-amber-500/20 px-2 py-1.5 rounded-sm">
                  <span className="text-zinc-400 font-semibold">{s.name}</span> — {d.by}: {d.reason} <span className="text-zinc-500 font-mono">· {fmtDate(d.at)}</span>
                </div>
              )))}
            </div>
          </div>
        </div>
      )}

      {/* Edit Order dialog */}
      <Dialog open={edit} onOpenChange={setEdit}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Edit Order</DialogTitle><DialogDescription className="text-zinc-500">Come back here anytime to fill in or fix anything you missed at intake — order details, reminders and detail tabs.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div><label className={label}>Order Name</label><Input value={ef.name || ""} onChange={(e) => setEf({ ...ef, name: e.target.value })} className={inputCls} data-testid="edit-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Customer</label><Input value={ef.customer || ""} onChange={(e) => setEf({ ...ef, customer: e.target.value })} className={inputCls} data-testid="edit-customer" /></div>
              <div><label className={label}>DVEPL Ref Code</label><Input value={ef.customer_code || ""} onChange={(e) => setEf({ ...ef, customer_code: e.target.value })} className={inputCls} data-testid="edit-customer-code" /></div>
              <div><label className={label}>Contact Person</label><Input value={ef.contact_person || ""} onChange={(e) => setEf({ ...ef, contact_person: e.target.value })} className={inputCls} data-testid="edit-contact-person" /></div>
              <div><label className={label}>Mobile No</label><Input value={ef.customer_whatsapp || ""} onChange={(e) => setEf({ ...ef, customer_whatsapp: e.target.value })} className={inputCls} data-testid="edit-customer-whatsapp" /></div>
              <div><label className={label}>Email</label><Input value={ef.customer_email || ""} onChange={(e) => setEf({ ...ef, customer_email: e.target.value })} className={inputCls} data-testid="edit-customer-email" /></div>
              <div><label className={label}>GST Number</label><Input value={ef.customer_gst || ""} onChange={(e) => setEf({ ...ef, customer_gst: e.target.value })} className={inputCls} data-testid="edit-customer-gst" /></div>
              <div className="col-span-2"><label className={label}>Billing Address</label><Input value={ef.billing_address || ""} onChange={(e) => setEf({ ...ef, billing_address: e.target.value })} className={inputCls} data-testid="edit-billing-address" /></div>
              <div className="col-span-2">
                <label className={label}>Shipping Address</label>
                <label className="flex items-center gap-2 text-xs text-zinc-400 mb-1.5 cursor-pointer">
                  <input type="checkbox" checked={efShipSame} onChange={(e) => { setEfShipSame(e.target.checked); if (e.target.checked) setEf({ ...ef, shipping_address: "" }); }} className="accent-emerald-600" data-testid="edit-shipping-same-as-billing" />
                  Same as Billing Address
                </label>
                {!efShipSame && (
                  <Input value={ef.shipping_address || ""} onChange={(e) => setEf({ ...ef, shipping_address: e.target.value })} placeholder="Delivery / job-site address for this order, if different" className={inputCls} data-testid="edit-shipping-address" />
                )}
              </div>
              <div><label className={label}>Customer PO No</label><Input value={ef.customer_po || ""} onChange={(e) => setEf({ ...ef, customer_po: e.target.value })} className={inputCls} data-testid="edit-customer-po" /></div>
              <div><label className={label}>Total Panels / Units</label><Input type="number" min="0" value={ef.total_units ?? ""} onChange={(e) => setEf({ ...ef, total_units: e.target.value ? Number(e.target.value) : "" })} placeholder="No. of physical panels (not item qty)" className={inputCls} data-testid="edit-total-units" /></div>
              <div><label className={label}>Date of Order</label><Input type="date" value={ef.order_date || ""} onChange={(e) => setEf({ ...ef, order_date: e.target.value })} className={inputCls} data-testid="edit-order-date" /></div>
              <div>
                <label className={label}>Order Taken By / Concerned Person</label>
                <Select value={ef.taken_by || ""} onValueChange={(v) => setEf({ ...ef, taken_by: v })}>
                  <SelectTrigger className={inputCls} data-testid="edit-taken-by"><SelectValue placeholder="Select team member" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">{(orderTakerIds.length ? users.filter((u) => orderTakerIds.includes(u.id)) : users).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className={label}>Date of Commitment</label>
                <div className="flex gap-1.5 mb-1.5">
                  <button type="button" onClick={() => setEf({ ...ef, commitment_mode: "fixed" })} className={`text-[11px] px-2 py-1 rounded-sm border transition-colors ${ef.commitment_mode !== "relative" ? "bg-emerald-600 text-white border-emerald-600 font-semibold" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"}`} data-testid="edit-commitment-mode-fixed">Fixed Date</button>
                  <button type="button" onClick={() => setEf({ ...ef, commitment_mode: "relative" })} className={`text-[11px] px-2 py-1 rounded-sm border transition-colors ${ef.commitment_mode === "relative" ? "bg-emerald-600 text-white border-emerald-600 font-semibold" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"}`} data-testid="edit-commitment-mode-relative">No. of Days</button>
                </div>
                {ef.commitment_mode === "relative" ? (
                  <div className="flex items-center gap-2">
                    <Input type="number" min="1" value={ef.commitment_days ?? ""} onChange={(e) => setEf({ ...ef, commitment_days: e.target.value ? parseInt(e.target.value, 10) : null })} placeholder="e.g. 45" className={inputCls} data-testid="edit-commitment-days" />
                    <span className="text-[11px] text-zinc-500 whitespace-nowrap">days from Drawing Approval</span>
                  </div>
                ) : (
                  <Input type="date" value={ef.target_dispatch || ""} onChange={(e) => setEf({ ...ef, target_dispatch: e.target.value })} className={inputCls} data-testid="edit-target-dispatch" />
                )}
              </div>
            </div>
            <div className="border border-zinc-800 p-3">
              <label className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer mb-2">
                <Checkbox checked={ef.reminders_enabled} onCheckedChange={(v) => setEf({ ...ef, reminders_enabled: !!v })} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="edit-reminders" />
                Send drawing-approval reminders until approved
              </label>
              {ef.reminders_enabled && (
                <div className="flex items-center gap-2 pl-6">
                  <span className="text-xs text-zinc-400">Every</span>
                  <Input type="number" min="1" value={ef.reminder_value} onChange={(e) => setEf({ ...ef, reminder_value: +e.target.value })} className={`${inputCls} h-8 w-20`} />
                  <Select value={ef.reminder_unit} onValueChange={(v) => setEf({ ...ef, reminder_unit: v })}>
                    <SelectTrigger className={`${inputCls} h-8 w-28`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50"><SelectItem value="hours">Hours</SelectItem><SelectItem value="days">Days</SelectItem></SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {customFields.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                {customFields.map((c) => (
                  <div key={c.id}>
                    <label className={label}>{c.required && <span className="text-red-400" title={c.hint || "This field is mandatory"}>* </span>}{c.label}</label>
                    {(c.type || "text") === "dropdown" ? (
                      <Select value={customVals[c.id] || ""} onValueChange={(v) => setCustomVals({ ...customVals, [c.id]: v })}>
                        <SelectTrigger className={inputCls} data-testid={`edit-custom-${c.id}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
                          {(c.options || "").split(",").map((op) => op.trim()).filter(Boolean).map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input type={c.type === "number" ? "number" : c.type === "date" ? "date" : "text"} value={customVals[c.id] || ""} onChange={(e) => setCustomVals({ ...customVals, [c.id]: e.target.value })} className={inputCls} data-testid={`edit-custom-${c.id}`} />
                    )}
                  </div>
                ))}
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-1"><label className={label}>Detail Tabs</label>
                <button onClick={() => setEf({ ...ef, details: [...(ef.details || []), { title: "", value: "" }] })} className="text-xs text-emerald-400 hover:underline">+ Add</button></div>
              {(ef.details || []).map((d, i) => (
                <div key={i} className="flex items-center gap-2 mb-1.5">
                  <Input value={d.title} onChange={(e) => efDetail(i, "title", e.target.value)} placeholder="Label" className={`${inputCls} h-8 w-40`} />
                  <Input value={d.value} onChange={(e) => efDetail(i, "value", e.target.value)} placeholder="Value" className={`${inputCls} h-8 flex-1`} />
                  <button onClick={() => setEf({ ...ef, details: ef.details.filter((_, idx) => idx !== i) })} className="text-zinc-600 hover:text-red-400"><Trash size={14} /></button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter><Button onClick={saveEdit} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="edit-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign All Stages dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-lg" data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Assign All Stages</DialogTitle>
            <DialogDescription className="text-zinc-500">Set or change the responsible person for every stage in one screen — pending, active or completed.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2 border border-zinc-800 bg-zinc-900/40 rounded-sm p-2.5">
            <div className="flex items-center gap-1.5 flex-1">
              <Select value={bulkOwnerAll} onValueChange={setBulkOwnerAll}>
                <SelectTrigger className={`${inputCls} h-8 flex-1 text-xs`} data-testid="bulk-assign-all-select"><SelectValue placeholder="Pick one person" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" disabled={!bulkOwnerAll} onClick={() => setBulkAssign(Object.fromEntries(stages.map((_, i) => [i, bulkOwnerAll])))}
                data-testid="bulk-assign-all-apply" className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-sm text-xs disabled:opacity-40">
                Apply to all
              </Button>
            </div>
          </div>
          <div className="space-y-1.5 max-h-[45vh] overflow-y-auto pr-1">
            {stages.map((s, i) => (
              <div key={s.key + i} className="flex items-center gap-2" data-testid={`bulk-assign-row-${i}`}>
                <span className="text-xs text-zinc-300 flex-1 truncate">{s.name}</span>
                <span className="text-[10px] text-zinc-500 shrink-0">{s.assignee_name || "Unassigned"}</span>
                <Select value={bulkAssign[i] || ""} onValueChange={(v) => setBulkAssign((p) => ({ ...p, [i]: v }))}>
                  <SelectTrigger className="w-36 h-8 bg-zinc-950 border-zinc-800 rounded-sm text-xs shrink-0" data-testid={`bulk-assign-select-${i}`}><SelectValue placeholder="Keep current" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
                    {users.map((u) => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={saveBulkAssign} data-testid="bulk-assign-save" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">Save Assignments</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PO Preview dialog */}
      <Dialog open={!!prev} onOpenChange={(op) => !op && setPrev(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">PO Preview — {prev?.order_no}</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {prev?.template_used ? "Using your uploaded stationery template." : "Using the default built-in PO layout."} Cell formatting in the Excel file may differ slightly.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-auto border border-zinc-800 rounded-sm" data-testid="po-preview-table">
            <table className="w-full text-xs">
              <tbody>
                {(prev?.rows || []).map((row, ri) => (
                  <tr key={ri} className="border-b border-zinc-800/50 last:border-0">
                    {row.map((c, ci) => (
                      <td key={ci} className={`px-2.5 py-1.5 ${typeof c === "number" ? "text-right font-mono text-zinc-200" : "text-zinc-300"}`}>
                        {typeof c === "number" ? c.toLocaleString() : c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button onClick={() => { setPrev(null); genPO(); }} data-testid="preview-download" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1"><FileArrowDown size={16} /> Download Excel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Task Reminders dialog */}
      <Dialog open={remOpen} onOpenChange={setRemOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Schedule Task Reminders</DialogTitle>
            <DialogDescription className="text-zinc-500">Remind the responsible person via Mobile App, WhatsApp or Email — before or after the task's due time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {remRows.map((r, i) => (
              <div key={i} className="flex items-center gap-2 flex-wrap border border-zinc-800 p-2 rounded-sm" data-testid={`rem-row-${i}`}>
                <Select value={r.channel} onValueChange={(v) => updRem(i, "channel", v)}>
                  <SelectTrigger className={`${inputCls} h-8 w-32 text-xs`} data-testid={`rem-channel-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
                    <SelectItem value="in_app">Mobile App</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" min="1" value={r.value} onChange={(e) => updRem(i, "value", e.target.value)} className={`${inputCls} h-8 w-16 text-xs`} data-testid={`rem-value-${i}`} />
                <Select value={r.unit} onValueChange={(v) => updRem(i, "unit", v)}>
                  <SelectTrigger className={`${inputCls} h-8 w-24 text-xs`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50"><SelectItem value="minutes">minutes</SelectItem><SelectItem value="hours">hours</SelectItem><SelectItem value="days">days</SelectItem></SelectContent>
                </Select>
                <Select value={r.when} onValueChange={(v) => updRem(i, "when", v)}>
                  <SelectTrigger className={`${inputCls} h-8 w-28 text-xs`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-50"><SelectItem value="before">Before due</SelectItem><SelectItem value="after">After due</SelectItem></SelectContent>
                </Select>
                <button onClick={() => setRemRows(remRows.filter((_, xi) => xi !== i))} className="text-zinc-600 hover:text-red-400"><X size={14} /></button>
              </div>
            ))}
            <button onClick={() => setRemRows([...remRows, { channel: "in_app", value: 10, unit: "minutes", when: "before" }])} className="text-xs text-emerald-400 hover:underline" data-testid="rem-add-more">+ Add More</button>
            <p className="text-[10px] text-zinc-600">Reminders fire relative to the task's due date/time. WhatsApp sends are simulated until Twilio credentials are connected.</p>
          </div>
          <DialogFooter><Button onClick={saveReminders} data-testid="rem-save" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revision dialog */}
      <Dialog open={showRev} onOpenChange={setShowRev}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50">
          <DialogHeader><DialogTitle className="font-heading">Request Drawing Revision</DialogTitle><DialogDescription className="text-zinc-500">Sends the workflow back to the drawing stage — the drawing person is responsible for changes.</DialogDescription></DialogHeader>
          <Textarea value={revNote} onChange={(e) => setRevNote(e.target.value)} className={inputCls} rows={3} placeholder="What changes did the customer request?" data-testid="rev-note" />
          <DialogFooter><Button onClick={requestRevision} className="bg-amber-500 hover:bg-amber-600 text-zinc-950 rounded-sm" data-testid="rev-submit">Send for Revision</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Skip Design & Nesting — reason dialog, so bypassing it is never silent */}
      <Dialog open={skipReasonOpen} onOpenChange={(v) => { setSkipReasonOpen(v); if (!v) setSkipReason(""); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50" data-testid="skip-design-dialog">
          <DialogHeader><DialogTitle className="font-heading">Skip Design & Nesting</DialogTitle><DialogDescription className="text-zinc-500">This order will go straight to Cutting. Explain why so it stays on record for anyone reviewing this order later.</DialogDescription></DialogHeader>
          <Textarea value={skipReason} onChange={(e) => setSkipReason(e.target.value)} className={inputCls} rows={3} placeholder="Why is Design/Nesting not needed for this order?" data-testid="skip-design-reason" />
          <DialogFooter><Button onClick={skipDesign} className="bg-amber-600 hover:bg-amber-700 rounded-sm" data-testid="skip-design-submit">Skip — Direct to Cutting</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Delay — offers quick-pick suggestions from previously-used reasons (across ALL
          orders) so wording stays consistent, which is what lets the Reports > "Delay Reasons"
          view actually group recurring bottlenecks instead of every entry being unique text. */}
      <Dialog open={delayFor !== null} onOpenChange={(v) => !v && setDelayFor(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-sm" data-testid="log-delay-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Log Delay</DialogTitle>
            <DialogDescription className="text-zinc-500">Why is "{delayFor !== null ? stages[delayFor]?.name : ""}" running behind? Pick a common reason or type your own.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {delaySuggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="log-delay-suggestions">
                {delaySuggestions.map((r, ri) => (
                  <button key={ri} type="button" onClick={() => setDelayReason(r)} data-testid={`log-delay-suggestion-${ri}`}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${delayReason === r ? "bg-amber-500/20 border-amber-500 text-amber-300" : "border-zinc-700 text-zinc-400 hover:border-amber-500/50 hover:text-amber-300"}`}>
                    {r}
                  </button>
                ))}
              </div>
            )}
            <Textarea value={delayReason} onChange={(e) => setDelayReason(e.target.value)} rows={3} placeholder="Reason for delay at this stage" className={inputCls} data-testid="log-delay-reason-input" />
          </div>
          <DialogFooter><Button onClick={submitDelay} disabled={!delayReason.trim()} className="bg-amber-600 hover:bg-amber-700 rounded-sm disabled:opacity-40" data-testid="log-delay-submit">Log Delay</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raise Check Request — any team member can tag ONE specific colleague to check the
          selected drawing(s) before they go out; that colleague then gets Approve/Reject rights
          on just those files (on top of Admin/Manager, who always have it). */}
      <Dialog open={checkReqOpen} onOpenChange={setCheckReqOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-sm" data-testid="check-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Raise a check request</DialogTitle>
            <DialogDescription className="text-zinc-500">Ask a specific colleague to check {selectedDrawings.length} selected drawing{selectedDrawings.length > 1 ? "s" : ""} before it's sent out. They'll get Approve/Reject rights on just these files.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={checkReviewer} onValueChange={setCheckReviewer}>
              <SelectTrigger className={inputCls} data-testid="check-request-reviewer"><SelectValue placeholder="Pick a colleague" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                {users.filter((u) => u.id !== user.id).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea value={checkNote} onChange={(e) => setCheckNote(e.target.value)} rows={3} placeholder="Note (optional)" className={inputCls} data-testid="check-request-note" />
          </div>
          <DialogFooter>
            <Button onClick={submitCheckRequest} data-testid="check-request-send" className="bg-amber-500 hover:bg-amber-600 text-zinc-950 rounded-sm">Send Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secure Drawing Link — a no-login public page the customer can open to View/Download,
          subscribe to "Alert me on changes" (emailed automatically when this drawing is revised),
          and Approve — with a one-click "Email to Customer" using this link. */}
      <Dialog open={!!shareDialog} onOpenChange={(v) => !v && setShareDialog(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-md" data-testid="share-drawing-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Share with Customer</DialogTitle>
            <DialogDescription className="text-zinc-500">
              A no-login link for "{shareDialog?.attachment?.name}" — the customer can view, download, ask to be alerted on changes, and approve it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className={label}>Public link</label>
              <div className="flex gap-2">
                <Input readOnly value={shareDialog?.url || ""} className={inputCls} data-testid="share-drawing-url" />
                <Button variant="outline" size="icon" className="border-zinc-800 shrink-0" data-testid="share-drawing-copy"
                  onClick={async () => {
                    const text = shareDialog?.url || "";
                    try {
                      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
                      else throw new Error("no clipboard api");
                    } catch {
                      const ta = document.createElement("textarea");
                      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
                      document.body.appendChild(ta); ta.select();
                      try { document.execCommand("copy"); } catch { /* ignore */ }
                      document.body.removeChild(ta);
                    }
                    toast.success("Link copied");
                  }}>
                  <Copy size={15} />
                </Button>
              </div>
            </div>
            <div>
              <label className={label}>Email to customer</label>
              <Input value={shareDialog?.emailTo || ""} onChange={(e) => setShareDialog((p) => ({ ...p, emailTo: e.target.value }))} placeholder="customer@example.com" className={inputCls} data-testid="share-drawing-email" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={sendShareEmail} disabled={sharingEmail || !shareDialog?.emailTo} data-testid="share-drawing-send" className="bg-sky-500 hover:bg-sky-600 text-zinc-950 rounded-sm gap-1.5">
              <PaperPlaneRight size={14} weight="fill" /> {sharingEmail ? "Sending…" : "Email Drawing Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compose dialog — one place for all email/WhatsApp correspondence with the customer or a
          vendor, with optional teammate tagging so the whole team stays in the loop. */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-lg" data-testid="compose-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Compose correspondence</DialogTitle>
            <DialogDescription className="text-zinc-500">Send an email or WhatsApp message and log it against {o.order_no}. WhatsApp sending is simulated/logged until a live provider is connected.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-1.5">
              {[["customer", "Customer"], ["vendor", "Vendor"], ["custom", "Custom"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setComposeForm((f) => ({ ...f, recipientType: k }))} data-testid={`compose-recipient-${k}`}
                  className={`flex-1 text-xs font-semibold py-1.5 rounded-sm border ${composeForm.recipientType === k ? "border-emerald-600 bg-emerald-600/15 text-emerald-400" : "border-zinc-800 bg-zinc-900 text-zinc-400"}`}>{l}</button>
              ))}
            </div>
            {composeForm.recipientType === "customer" && (
              <div className="text-xs text-zinc-500 border border-zinc-800 bg-zinc-900/40 rounded-sm px-3 py-2">
                {o.customer || o.name} — {o.customer_email || "no email on file"} · {o.customer_whatsapp || "no WhatsApp number on file"}
              </div>
            )}
            {composeForm.recipientType === "vendor" && (
              <Select value={composeForm.vendorId} onValueChange={(v) => setComposeForm((f) => ({ ...f, vendorId: v }))}>
                <SelectTrigger className={inputCls} data-testid="compose-vendor-select"><SelectValue placeholder="Pick a vendor" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {vendorRegistry.map((v) => <SelectItem key={v.id} value={v.id}>{vTitle(v)}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {composeForm.recipientType === "custom" && (
              <div className="grid grid-cols-2 gap-2">
                <Input value={composeForm.customEmail} onChange={(e) => setComposeForm((f) => ({ ...f, customEmail: e.target.value }))} placeholder="Email" className={inputCls} data-testid="compose-custom-email" />
                <Input value={composeForm.customPhone} onChange={(e) => setComposeForm((f) => ({ ...f, customPhone: e.target.value }))} placeholder="WhatsApp number" className={inputCls} data-testid="compose-custom-phone" />
              </div>
            )}
            <Input value={composeForm.subject} onChange={(e) => setComposeForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Subject (used for email)" className={inputCls} data-testid="compose-subject" />
            <Textarea value={composeForm.text} onChange={(e) => setComposeForm((f) => ({ ...f, text: e.target.value }))} rows={4} placeholder="Write your message…" className={inputCls} data-testid="compose-text" />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 text-xs text-zinc-300 cursor-pointer"><Checkbox checked={composeForm.email} onCheckedChange={(v) => setComposeForm((f) => ({ ...f, email: !!v }))} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="compose-channel-email" /> Email</label>
              <label className="flex items-center gap-1.5 text-xs text-zinc-300 cursor-pointer"><Checkbox checked={composeForm.whatsapp} onCheckedChange={(v) => setComposeForm((f) => ({ ...f, whatsapp: !!v }))} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="compose-channel-whatsapp" /> WhatsApp</label>
            </div>
            <div>
              <label className={label}>Tag teammates (optional)</label>
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {composeForm.taggedIds.map((tid) => (
                  <span key={tid} className="text-xs bg-zinc-800 text-zinc-200 px-2 py-1 rounded-sm flex items-center gap-1" data-testid={`compose-tag-${tid}`}>
                    {users.find((u) => u.id === tid)?.name || tid}
                    <button type="button" onClick={() => setComposeForm((f) => ({ ...f, taggedIds: f.taggedIds.filter((x) => x !== tid) }))} className="text-zinc-500 hover:text-red-400"><X size={10} /></button>
                  </span>
                ))}
              </div>
              <Select value="" onValueChange={(v) => setComposeForm((f) => ({ ...f, taggedIds: f.taggedIds.includes(v) ? f.taggedIds : [...f.taggedIds, v] }))}>
                <SelectTrigger className={`${inputCls} h-8 text-xs`} data-testid="compose-tag-select"><SelectValue placeholder="+ Tag a teammate" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {users.filter((u) => !composeForm.taggedIds.includes(u.id)).map((u) => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {composeForm.taggedIds.length > 0 && (
                <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer mt-2">
                  <Checkbox checked={composeForm.ccTag} onCheckedChange={(v) => setComposeForm((f) => ({ ...f, ccTag: !!v }))} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="compose-cc-tagged" /> Also CC tagged teammates on the email (otherwise they just get an in-app alert)
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={sendCompose} data-testid="compose-send" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1"><PaperPlaneRight size={15} weight="fill" /> Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function s_cat(s) {
  return (s.category || TYPE_LABEL[s.type] || "Stage").toUpperCase();
}
